// The room's lights in the plan: the Cove, Channel, Spot, Starry sky and Strip
// tools, their hovers, drags, grips and typed figures, the fibre kits' projector,
// the light tags, several lights picked together, and the lights' panels.
// docs/claude/lights.md.
import { BUILTIN_PRODUCTS } from '../../shared/builtinProducts';
import { unreachable } from '../../shared/exhaustive';
import { productName } from '../../shared/names';
import {
  type Backrest,
  type ChannelAnchor,
  type ProductInstance,
  type ProductTemplate,
  type RoomLight,
  type StripHost,
  type StripPos,
  type Vec2,
} from '../../shared/types';
import {
  backrestFigures,
  backrestRun,
  backrestsOf,
  backrestSpanFor,
  backrestStops,
  facePoint,
} from '../backrest';
import {
  channelHangNeeded,
  frontBoard,
  frontTakesChannel,
} from '../benchSolids';
import {
  CHANNEL_FIGURE_OPTIONS,
  CHANNEL_OPTIONS,
  type ToolOption,
} from '../commands';
import {
  beginGesture,
  drawingRev,
  dropDrag,
  flashStatus,
  instLocked,
  invalidate,
  newId,
  objHidden,
  objUnpickable,
  pushHistory,
  renderProperties,
  startDrag,
  state,
  updateStatus,
  vp,
} from './core';
import { drawingDimStyle } from '../dimFormat';
import { openFigureEditor } from './dimEditor';
import {
  draftSeg,
  instanceToWorld,
  pointInPolygon,
  toInstanceLocal,
} from '../geometry';
import { roomBase } from '../heights';
import {
  getUiLang,
  tr,
} from '../i18n';
import {
  type FibreKit,
  isFibreKit,
} from '../lightLife';
import {
  A1_SKIRTING_SET_BACK,
  backrestLighting,
  ceilingUnder,
  chainedAnchor,
  CHANNEL_END_GAP,
  channelAlign,
  channelAnchorPath,
  channelAt,
  type ChannelDim,
  channelDims,
  channelEndGap,
  channelFigures,
  type ChannelLight,
  channelRuns,
  channelsOf,
  type ChannelWarning,
  channelWarnings,
  circleRing,
  COVE_DEFAULTS,
  coveFigures,
  coveKind,
  type CoveLight,
  coveOf,
  coveWallAt,
  defaultLightProductId,
  distributedPositions,
  FIBRE_DIAMETER,
  fibreArcTargets,
  FIBRES_PER_ARC,
  furthestFibre,
  hostPositions,
  isRingPath,
  kitArcs,
  kitFibres,
  lightLength,
  lightProduct,
  lightTagAnchors,
  lightTagKey,
  lightWatts,
  moveChannel,
  nearestWall,
  pathOutline,
  PROJECTOR_SIZE,
  projectorBase,
  projectorHome,
  projectorMiddle,
  projectorOutside,
  roomAxes,
  setBackrestLighting,
  setChannelGap,
  setLightProduct,
  shiftChannelSegment,
  skiesOf,
  SKY_FIBRE_DIAMETER,
  skyCount,
  skyFibres,
  skyFigures,
  type SkyLight,
  skyMarginHandles,
  skyZone,
  SPOT_DIAMETER,
  spotAlign,
  spotAt,
  spotFigures,
  type SpotGuide,
  type SpotLight,
  spotMount,
  spotsOf,
  STRIP_INSET,
  STRIP_POSITIONS,
  STRIP_WIDTH,
  type StripFreeEnd,
  stripInsetAt,
  type StripLight,
  stripPieces,
  stripRuns,
  stripsOf,
  type StripTarget,
  stripTargetAt,
  syncChannel,
  trimPath,
  type Vec3,
  wallInnerFace,
} from '../lights';
import { dropGoneInstancesFromSelection } from './partsTab';
import {
  isPolyRoom,
  polyInnerCarved,
  resolvedOutline,
} from '../polyRoom';
import {
  instanceCorners,
  type LightTagHit,
  paintSpotAid,
  type RenderOptions,
  type SpotAidDraw,
  spotFigureAt,
} from '../render';
import {
  dropFeature,
  featureOf,
  selectFeature,
  selectOne,
  selectRoomKeepingFeature,
} from '../selection';
import {
  type BenchHover,
  canvas,
  clickPolyline,
  copyPorts,
  deselectAll,
  downGrip,
  noteChangeInStep,
  planHits,
  rcpRoom,
  selectedInstance,
  setTool,
  SNAP_PX,
  snapDraftPoint,
} from '../main';
import { removeObjectsById } from './deleteSelection';
import {
  type PolyBench,
  type ProductBenchLeg,
} from '../roomBench';
import {
  fixtureHitTest,
  removeFixture,
  selectedFixtureNow,
} from './roomFixtures';
import { updateDistEntry } from './distanceEntry';
import { snapPoint } from './snapSet';
import {
  aimedBench,
  aimedFixture,
  pxAt,
  type ToolAim,
} from '../toolAim';
import {
  buttonRow,
  deleteRow,
  infoRow,
  noteRow,
  numberRow,
  parseMeasure,
  sectionTitle,
  selectRow,
} from './panelRows';
import {
  gripAt,
  type SelGrip,
} from './grips';
import { type ObjRef } from '../pickStack';
import { traitsOf } from '../../shared/categories';

/** This module's state that main.ts reads and writes too. */
export const lightTools = {
  /** A channel being drawn (world), its room and where it is drawn there — the
   *  plan, or a ceiling plan moved by `channelShift`. */
  channelDraft: [] as Vec2[],
  channelRoomId: null as string | null,
  skyDraft: [] as Vec2[],
  skyRoomId: null as string | null,
  skyToolDraw: false,
  /** The Starry sky tool: the kit it lays, whether its zone is drawn by hand (typed
   *  Z) rather than the whole ceiling, the zone being drawn (world) and its room —
   *  and, from a sky's panel, the sky whose zone is being drawn again. */
  skyToolKit: 100 as 50 | 100,
  /** The Spot tool puts single fibre ends (typed F) instead of spots. */
  spotToolFibre: false,
  /** The Strip tool's Up + down: a click on a backrest puts both its strips. */
  stripToolBoth: false,
  /** The position the Strip tool puts, typed as one of its options; null takes its
   *  way's own: down, A1 on a bench and D on a backrest; up, B and C. */
  stripToolPos: null as StripPos | null,
  stripToolHoverKey: null as string | null,
  /** How the Channel tool lays its channels: drawn click by click, along the walls
   *  clicked near (a click on the wall after the last one laid carries it round the
   *  corner), round the room (`perimeter`, a ring), down its middle, or in runs. */
  channelMode: 'free' as 'free' | 'wall' | 'perimeter' | 'centre' | 'runs',
  channelShift: { x: 0, y: 0 } as Vec2,
  /** The Channel tool's figures and holds, where they are drawn (null: none). */
  channelAid: null as { instId: string; shift: Vec2; paths: Vec2[][]; except?: string; from: Vec2[]; guides: SpotGuide[]; at?: Vec2 } | null,
  /** What a number typed next sets: the figure an option picked, else — laying
   *  runs — how many. */
  channelInput: null as 'gap' | 'margin' | 'offset' | 'spacing' | null,
  /** The channel laid along walls last: a click on the wall after its last (or
   *  before its first) carries it on, Shift+click lays a new one. */
  channelChain: null as { instId: string; id: string } | null,
  skyRezoneId: null as string | null,
  /** The Spot tool's or a spot drag's figures — the selected spot's when null. */
  spotAid: null as SpotAid | null,
  /** Alt held at the last move: the Spot tool puts a spot where the cursor is. */
  spotFree: false,
  /** A light's tag being dragged: its room and the light keeping its offset, where
   *  it was grabbed (room local) and its offset then, the press on screen and
   *  whether it has moved yet. */
  lightTagDrag: null as { instId: string; id: string; grab: Vec2; o0: Vec2; sp0: Vec2; moved: boolean } | null,
};

/** How far in from a wall's inner face the Cove tool takes the wall (mm). */
const COVE_REACH = 600;

/** The wall the Cove tool would add to (or take out of) its room's cove. */
function coveToolTarget(world: Vec2): { room: ProductInstance; wall: number; on: boolean } | null {
  for (let i = state.drawing.instances.length - 1; i >= 0; i--) {
    const room = state.drawing.instances[i];
    if (!room.autoBenches || instLocked(room) || !isPolyRoom(room)) continue;
    const local = toInstanceLocal(room, world);
    if (!pointInPolygon(local, polyInnerCarved(room))) continue;
    if (!coveKind(room)) return null;
    const wall = coveWallAt(room, local, COVE_REACH);
    return wall == null ? null : { room, wall, on: !!coveOf(room)?.walls.includes(wall) };
  }
  return null;
}

/** The Cove tool's hover: the band along the wall a click adds (green) or takes out (red). */
export function coveHoverAt(aim: ToolAim): BenchHover | null {
  const world = aim.world;
  const t = coveToolTarget(world);
  const f = t ? wallInnerFace(t.room, t.wall) : null;
  if (!t || !f) return null;
  const deep = coveKind(t.room) === 'layer' ? (coveOf(t.room) ? coveFigures(coveOf(t.room)!).gap : COVE_DEFAULTS.gap) : 70;
  const at = (p: Vec2, o: number): Vec2 => ({ x: p.x + f.n.x * o, y: p.y + f.n.y * o });
  return { instId: t.room.id, polys: [[f.a, f.b, at(f.b, deep), at(f.a, deep)]], kind: t.on ? 'delete' : 'add' };
}

export function downCoveTool(e: MouseEvent, aim: ToolAim): boolean {
  const world = aim.world;
  if (state.tool !== 'light-cove' || e.button !== 0) return false;
  const t = coveToolTarget(world);
  if (!t) return true;
  pushHistory();
  const { room, wall } = t;
  let cove = coveOf(room);
  if (!cove) {
    cove = { id: newId('lt'), kind: 'cove', walls: [] };
    (room.lights ??= []).push(cove);
  }
  cove.walls = t.on ? cove.walls.filter((w) => w !== wall) : [...cove.walls, wall].sort((a, b) => a - b);
  deselectAll();
  selectOne(state, 'instance', room.id);
  if (cove.walls.length) selectFeature(state, { kind: 'fixture', instId: room.id, id: cove.id });
  else removeFixture(room, cove.id);
  state.benchAddHover = coveHoverAt(aim);
  renderProperties();
  invalidate();
  return true;
}

/** A cove's panel: its walls, how the ceiling takes it — a flat one's second
 *  layer with its figures, or a Coved one's springing line —, its strip. */
function renderCovePanel(body: HTMLElement, inst: ProductInstance, light: CoveLight): void {
  const edit = (fn: () => void): void => { pushHistory(); fn(); renderProperties(); invalidate(); };
  const kind = coveKind(inst);
  body.appendChild(sectionTitle('Cove light'));
  body.appendChild(infoRow('Walls', light.walls.map((w) => `${tr('wall')} ${w + 1}`).join(', ')));
  body.appendChild(infoRow('Ceiling', tr(kind === 'layer' ? 'Flat: a second layer under it' : kind === 'springing' ? 'Coved: on the springing line' : 'Takes no cove')));
  if (kind === 'layer') {
    const f = coveFigures(light);
    const fig = (label: string, v: number, set: (x: number) => void): void => {
      body.appendChild(numberRow(label, v, (x) => edit(() => { light.secondLayer = { ...light.secondLayer }; set(Math.max(0, Math.round(x))); }), { commit: true }));
    };
    fig('Gap from the walls (mm)', f.gap, (x) => { light.secondLayer!.gap = x; });
    fig('Ledge (mm)', f.ledge, (x) => { light.secondLayer!.ledge = x; });
    fig('Ledge thickness (mm)', f.ledgeThickness, (x) => { light.secondLayer!.ledgeThickness = x; });
    fig('Valance (mm)', f.valance, (x) => { light.secondLayer!.valance = x; });
    body.appendChild(infoRow('Opening', `${Math.round(f.gap - f.ledge)} mm`));
  }
  const len = lightLength(inst, light);
  body.appendChild(infoRow('Length', `${(len / 1000).toFixed(2)} m`));
  body.appendChild(selectRow('Colour', [['2700K', '2700 K white'], ['RGB', 'RGB']], light.colour ?? '2700K', (v) => edit(() => {
    if (v === 'RGB') light.colour = 'RGB'; else delete light.colour;
    setLightProduct(light, undefined);
  })));
  body.appendChild(lightProductRow(light, 'strip'));
  body.appendChild(infoRow('Power', `${lightWatts(light, len).toFixed(1)} W`));
  lightCommonRows(body, inst, light);
  body.appendChild(deleteRow(() => { pushHistory(); removeFixture(inst, light.id); renderProperties(); invalidate(); }));
}

// ---- Room fixtures: channels (the Channel tool) --------------------------------

let channelRunCount = 3;
const CHANNEL_MAX_RUNS = 12;
/** The tool's figures, typed: how far in from the walls (null: as far in as the
 *  click), the gap at the ends, the runs' spacing and margin (null: by count, in
 *  equal shares), and whether runs go across the room. */
let channelOffset: number | null = null;
let channelGap = CHANNEL_END_GAP;
let channelSpacing: number | null = null;
let channelMargin: number | null = null;
let channelAcross = false;
/** How near (px) a channel's point must come to a line it is held on. */
const CHANNEL_ALIGN_PX = 10;

/** The Channel tool's options: its layouts, then the figures of the one in use. */
export function channelToolOptions(): ToolOption[] {
  const F = CHANNEL_FIGURE_OPTIONS, way = channelAcross ? F.along : F.across;
  const figures: Record<typeof lightTools.channelMode, ToolOption[]> = {
    free: [],
    wall: [F.offset, F.gap],
    perimeter: [F.offset],
    centre: [way, F.gap],
    runs: [way, F.spacing, F.margin, F.gap],
  };
  return [...CHANNEL_OPTIONS, ...figures[lightTools.channelMode]];
}

/** A Channel tool option typed: a layout (typed again: back to drawing), the way
 *  runs go, or the figure the number typed next sets — typed again, the offset
 *  goes back to the click's and the margin to none. */
export function applyChannelOption(opt: ToolOption): boolean {
  const id = opt.id;
  if (id === 'wall' || id === 'perimeter' || id === 'centre' || id === 'runs') {
    lightTools.channelMode = lightTools.channelMode === id ? 'free' : id;
    lightTools.channelInput = null;
  } else if (id === 'across' || id === 'along') channelAcross = id === 'across';
  else if (id === 'offset' || id === 'gap' || id === 'spacing' || id === 'margin') {
    if (lightTools.channelInput === id && id === 'offset') { channelOffset = null; lightTools.channelInput = null; }
    else if (lightTools.channelInput === id && id === 'margin') { channelMargin = null; lightTools.channelInput = null; }
    else lightTools.channelInput = id;
  } else return false;
  updateStatus(); invalidate();
  return true;
}

/** What a number typed into the bar sets, the Channel tool armed with no draft. */
export function channelNumberTarget(): 'count' | 'gap' | 'margin' | 'offset' | 'spacing' | null {
  if (state.tool !== 'light-channel' || lightTools.channelDraft.length) return null;
  return lightTools.channelInput ?? (lightTools.channelMode === 'runs' ? 'count' : null);
}

/** A number typed for the Channel tool: the figure it was asked for. */
export function setChannelNumber(text: string): boolean {
  const target = channelNumberTarget();
  if (!target) return false;
  if (target === 'count') {
    if (!/^\d+$/.test(text)) return false;
    channelRunCount = Math.min(CHANNEL_MAX_RUNS, Math.max(1, Number(text)));
    channelSpacing = null;
  } else {
    const v = parseMeasure(text);
    if (v == null || !(v >= 0)) return false;
    if (target === 'offset') channelOffset = Math.round(v);
    else if (target === 'gap') channelGap = Math.round(v);
    else if (target === 'margin') channelMargin = Math.round(v);
    else if (v > 0) channelSpacing = Math.round(v);
  }
  lightTools.channelInput = null;
  updateStatus(); invalidate();
  return true;
}

/** The room whose ceiling a point is under: the topmost one that can be edited. */
function channelRoomAt(world: Vec2): ProductInstance | null {
  for (let i = state.drawing.instances.length - 1; i >= 0; i--) {
    const room = state.drawing.instances[i];
    if (!room.autoBenches || instLocked(room) || !isPolyRoom(room)) continue;
    if (pointInPolygon(toInstanceLocal(room, world), polyInnerCarved(room))) return room;
  }
  return null;
}

/** Where a world point falls on a ceiling the Channel tool can lay in: the plan's
 *  or a ceiling plan's (`ceilingPlacesAt`), inside the room's inner outline. */
function channelPlaceAt(world: Vec2): CeilingPlace | null {
  return ceilingPlacesAt(world).find((pl) => pointInPolygon(pl.local, polyInnerCarved(pl.room))) ?? null;
}

/** A point of a channel being drawn: snapped as a line's (OSNAP, ortho, polar) —
 *  and, no OSNAP point there and Alt up, held on the lines a channel lines up
 *  with (`channelAlign`), the axis a square segment keeps left free. */
function channelSnap(world: Vec2, alt: boolean): { at: Vec2; from: Vec2[]; guides: SpotGuide[] } {
  const q = snapDraftPoint(world, [...lightTools.channelDraft], { shift: alt, allow45: true, lastSeg: draftSeg(lightTools.channelDraft) });
  const pl = lightTools.channelRoomId ? null : channelPlaceAt(world);
  const room = lightTools.channelRoomId ? state.drawing.instances.find((i) => i.id === lightTools.channelRoomId) : pl?.room;
  const shift = lightTools.channelRoomId ? lightTools.channelShift : pl?.shift ?? { x: 0, y: 0 };
  if (!room || alt || state.snapIndicator) return { at: q, from: [], guides: [] };
  const local = toInstanceLocal(room, { x: q.x - shift.x, y: q.y - shift.y });
  const skip: number[] = [];
  const last = lightTools.channelDraft.at(-1);
  if (last) {
    const l = toInstanceLocal(room, { x: last.x - shift.x, y: last.y - shift.y }), { u, v } = roomAxes(room);
    const d = { x: local.x - l.x, y: local.y - l.y };
    if (Math.abs(d.x * v.x + d.y * v.y) < 0.5) skip.push(1);
    if (Math.abs(d.x * u.x + d.y * u.y) < 0.5) skip.push(0);
  }
  const held = channelAlign(room, local, CHANNEL_ALIGN_PX / vp.scale, { skip });
  if (!held.from.length && !held.guides.length) return { at: q, from: [], guides: [] };
  const w = instanceToWorld(room, held.at);
  return { at: { x: w.x + shift.x, y: w.y + shift.y }, from: held.from, guides: held.guides };
}

/** What a click lays where it is (room local), by the tool's layout: the paths, a
 *  laid-along-walls one's anchor, and the channel it carries on (`extend`). */
function channelLayout(room: ProductInstance, local: Vec2, fresh = false): { paths: Vec2[][]; anchor?: ChannelAnchor; extend?: ChannelLight } {
  const near = (): number => { const w = nearestWall(room, local); return channelOffset ?? (w ? Math.max(0, Math.round(w.offset / 10) * 10) : 0); };
  if (lightTools.channelMode === 'wall') {
    const w = nearestWall(room, local);
    if (!w) return { paths: [] };
    const chain = lightTools.channelChain?.instId === room.id && !fresh ? channelsOf(room).find((c) => c.id === lightTools.channelChain!.id && c.anchor) : undefined;
    const carried = chain?.anchor ? chainedAnchor(room, chain.anchor, w.wall) : null;
    if (chain && carried) {
      const pts = channelAnchorPath(room, carried);
      if (pts) return { paths: [pts], anchor: carried, extend: chain };
    }
    const anchor: ChannelAnchor = { walls: [w.wall], offset: near(), startGap: channelGap, endGap: channelGap };
    const pts = channelAnchorPath(room, anchor);
    return pts ? { paths: [pts], anchor } : { paths: [] };
  }
  if (lightTools.channelMode === 'perimeter') {
    const anchor: ChannelAnchor = { walls: [], offset: near(), closed: true };
    const pts = channelAnchorPath(room, anchor);
    return pts ? { paths: [pts], anchor } : { paths: [] };
  }
  if (lightTools.channelMode === 'centre') return { paths: channelRuns(room, 1, { across: channelAcross, gap: channelGap }) };
  if (lightTools.channelMode === 'runs') {
    return { paths: channelRuns(room, channelRunCount, { across: channelAcross, gap: channelGap, margin: channelMargin ?? undefined, spacing: channelSpacing ?? undefined }) };
  }
  return { paths: [] };
}

/** The Channel tool's hover: a channel under the cursor (red: a click removes it),
 *  else the channels a laid-out click would add (green) with their figures;
 *  drawing one freely, the snapped point, held where it lines up, the figures of
 *  the channel so far. In the plan or a ceiling plan. */
export function channelHoverAt(e: MouseEvent, world: Vec2): BenchHover | null {
  // Its figures and holds are painted with the plan: a change repaints it.
  const was = JSON.stringify(lightTools.channelAid);
  const hover = channelHoverOf(e, world);
  if (JSON.stringify(lightTools.channelAid) !== was) invalidate();
  return hover;
}
function channelHoverOf(e: MouseEvent, world: Vec2): BenchHover | null {
  lightTools.channelAid = null;
  const drawing = lightTools.channelMode === 'free' || lightTools.channelDraft.length > 0;
  let held: ReturnType<typeof channelSnap> | null = null;
  if (drawing) {
    held = channelSnap(world, e.altKey);
    state.hoverWorld = held.at;
    updateDistEntry();
  }
  const pl = lightTools.channelRoomId ? null : channelPlaceAt(world);
  const room = lightTools.channelRoomId ? state.drawing.instances.find((i) => i.id === lightTools.channelRoomId) ?? null : pl?.room ?? null;
  if (!room) return null;
  const shift = lightTools.channelRoomId ? lightTools.channelShift : pl!.shift;
  const toLocal = (p: Vec2): Vec2 => toInstanceLocal(room, { x: p.x - shift.x, y: p.y - shift.y });
  const local = toLocal(world);
  if (drawing && held) {
    const path = lightTools.channelDraft.length ? [...lightTools.channelDraft, held.at].map(toLocal) : [];
    lightTools.channelAid = { instId: room.id, shift, paths: path.length >= 2 ? [path] : [], from: held.from, guides: held.guides, at: toLocal(held.at) };
    return null;
  }
  const on = channelAt(room, local, 4 / vp.scale);
  const w = Math.max(CHANNEL_HOVER_MM, 8 / vp.scale);
  if (on) return { instId: room.id, polys: [pathOutline(on.pts, w)], kind: 'delete', shift };
  const laid = channelLayout(room, local, e.shiftKey);
  if (!laid.paths.length) return null;
  lightTools.channelAid = { instId: room.id, shift, paths: laid.paths, except: laid.extend?.id, from: [], guides: [] };
  return { instId: room.id, polys: laid.paths.map((pts) => pathOutline(pts, w)), kind: 'add', shift };
}
const CHANNEL_HOVER_MM = 20;

export function downChannelTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool !== 'light-channel' || e.button !== 0) return false;
  if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
  if (!lightTools.channelDraft.length) {
    const pl = channelPlaceAt(world);
    if (!pl) return true;
    const { room, local } = pl;
    const on = channelAt(room, local, 4 / vp.scale);
    if (on) {
      pushHistory();
      removeFixture(room, on.id);
      selectRoomKeepingFeature(state, room.id);
      state.benchAddHover = channelHoverAt(e, world);
      renderProperties(); invalidate();
      return true;
    }
    if (lightTools.channelMode !== 'free') {
      const laid = channelLayout(room, local, e.shiftKey);
      if (laid.extend && laid.anchor) carryChannel(room, laid.extend, laid.anchor);
      else if (laid.paths.length) addChannels(room, laid.paths, laid.anchor);
      state.benchAddHover = channelHoverAt(e, world);
      return true;
    }
    lightTools.channelRoomId = room.id;
    lightTools.channelShift = pl.shift;
  }
  clickPolyline(channelSnap(world, e.altKey).at);
  updateStatus();
  invalidate();
  return true;
}

/** Add channels (room local paths) to a room, one undo step, the last selected; a
 *  path laid along walls keeps its `anchor` (an open one can be carried on). */
function addChannels(room: ProductInstance, paths: Vec2[][], anchor?: ChannelAnchor): void {
  pushHistory();
  let last: ChannelLight | null = null;
  for (const pts of paths) {
    last = { id: newId('lt'), kind: 'channel', pts: pts.map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) })), ...(anchor && paths.length === 1 ? { anchor: { ...anchor } } : {}) };
    (room.lights ??= []).push(last);
  }
  lightTools.channelChain = last?.anchor && !last.anchor.closed ? { instId: room.id, id: last.id } : null;
  deselectAll();
  selectOne(state, 'instance', room.id);
  if (last) selectFeature(state, { kind: 'fixture', instId: room.id, id: last.id });
  state.benchAddHover = null;
  renderProperties();
  invalidate();
}

/** The channel laid along walls last, carried on round a corner: one undo step. */
function carryChannel(room: ProductInstance, ch: ChannelLight, anchor: ChannelAnchor): void {
  pushHistory();
  ch.anchor = { ...anchor };
  syncChannel(room, ch);
  deselectAll();
  selectOne(state, 'instance', room.id);
  selectFeature(state, { kind: 'fixture', instId: room.id, id: ch.id });
  renderProperties();
  invalidate();
}

/** The channel drawn click by click goes into its room; the tool stays armed. */
export function finishChannel(): void {
  const room = lightTools.channelRoomId ? state.drawing.instances.find((i) => i.id === lightTools.channelRoomId) : null;
  const pts = lightTools.channelDraft, shift = lightTools.channelShift;
  lightTools.channelDraft = []; lightTools.channelRoomId = null; lightTools.channelShift = { x: 0, y: 0 }; lightTools.channelAid = null;
  if (room && pts.length >= 2) addChannels(room, [pts.map((p) => toInstanceLocal(room, { x: p.x - shift.x, y: p.y - shift.y }))]);
  updateStatus();
  invalidate();
}

/** The Channel tool's prompt, by its layout, its figures and its draft. */
export function channelToolHint(): string {
  if (lightTools.channelDraft.length) return tr('channel light — click to add points; Enter, right-click or double-click to finish');
  const mm = (v: number): string => `${Math.round(v)} mm`;
  if (lightTools.channelInput === 'offset') return tr('channel light — type how far in from the walls (mm); the option again: as far in as the click');
  if (lightTools.channelInput === 'gap') return tr('channel light — type the gap at its ends (mm)');
  if (lightTools.channelInput === 'spacing') return tr('channel light — type the runs’ spacing (mm)');
  if (lightTools.channelInput === 'margin') return tr('channel light — type the outer runs’ margin from the walls (mm); the option again: none');
  if (lightTools.channelMode === 'wall') {
    return channelOffset == null ? tr('channel light — click near a wall to lay a channel along it, as far in as the click; then the wall next to it carries it on (Shift: a new one)')
      : tr('channel light — click near a wall to lay a channel along it, {d} in; then the wall next to it carries it on (Shift: a new one)').replace('{d}', mm(channelOffset));
  }
  if (lightTools.channelMode === 'perimeter') {
    return channelOffset == null ? tr('channel light — click in a room to lay a channel round it, as far in as the click')
      : tr('channel light — click in a room to lay a channel round it, {d} in').replace('{d}', mm(channelOffset));
  }
  if (lightTools.channelMode === 'centre') return tr('channel light — click a room to lay a channel down its middle');
  if (lightTools.channelMode === 'runs') {
    if (channelSpacing != null) return tr('channel light — click a room to lay runs {s} apart across it').replace('{s}', mm(channelSpacing));
    return tr('channel light — click a room to lay {n} runs across it (type a number for more or fewer)').replace('{n}', String(channelRunCount));
  }
  return tr('channel light — click in a room’s ceiling to start a channel; click one to remove it');
}

/** What a channel runs into, as its panel says it. */
const CHANNEL_WARNING_TEXT: Record<ChannelWarning, () => string> = {
  cove: () => tr('It runs into the cove.'),
  heater: () => tr('It comes within 300 mm of the heater.'),
  ridge: () => tr('It crosses a ridge or a change of slope in the ceiling.'),
  spot: () => tr('It runs into a spot.'),
  wall: () => tr('It is too near a wall for its profile’s flange.'),
};

/** The plan footprints (room local) of the heaters standing in a room. */
export function heaterFootprints(room: ProductInstance): Vec2[][] {
  const outline = resolvedOutline(room, 0);
  return state.drawing.instances
    .filter((h) => traitsOf(h.category).guarded && pointInPolygon(toInstanceLocal(room, { x: h.x, y: h.y }), outline))
    .map((h) => instanceCorners(h).map((c) => toInstanceLocal(room, c)));
}

/** A channel's panel: its groove, length and power, colour and product. */
function renderChannelPanel(body: HTMLElement, inst: ProductInstance, light: ChannelLight): void {
  const edit = (fn: () => void): void => { pushHistory(); fn(); renderProperties(); invalidate(); };
  const f = channelFigures(light);
  body.appendChild(sectionTitle('Channel light'));
  body.appendChild(numberRow('Width (mm)', f.width, (v) => edit(() => { light.width = Math.max(4, Math.round(v)); }), { commit: true }));
  body.appendChild(numberRow('Depth (mm)', f.depth, (v) => edit(() => { light.depth = Math.max(4, Math.round(v)); }), { commit: true }));
  const len = lightLength(inst, light);
  body.appendChild(infoRow('Length', `${(len / 1000).toFixed(2)} m`));
  body.appendChild(selectRow('Colour', [['2700K', '2700 K white'], ['RGB', 'RGB']], light.colour ?? '2700K', (v) => edit(() => {
    if (v === 'RGB') light.colour = 'RGB'; else delete light.colour;
    setLightProduct(light, undefined);
  })));
  body.appendChild(lightProductRow(light, 'strip'));
  body.appendChild(infoRow('Power', `${lightWatts(light, len).toFixed(1)} W`));
  // Laid along walls: its walls, how far in, its gaps — it follows them.
  const a = light.anchor;
  if (a) {
    const keep = (fn: () => void): void => edit(() => { fn(); syncChannel(inst, light); });
    body.appendChild(infoRow('Walls', a.closed ? tr('Every wall') : a.walls.map((w) => `${tr('wall')} ${w + 1}`).join(', ')));
    body.appendChild(numberRow('Offset from the walls (mm)', a.offset, (v) => keep(() => { a.offset = Math.max(0, Math.round(v)); }), { commit: true }));
    if (!a.closed) {
      body.appendChild(numberRow('Gap at the start (mm)', a.startGap ?? CHANNEL_END_GAP, (v) => keep(() => { a.startGap = Math.max(0, Math.round(v)); }), { commit: true }));
      body.appendChild(numberRow('Gap at the end (mm)', a.endGap ?? CHANNEL_END_GAP, (v) => keep(() => { a.endGap = Math.max(0, Math.round(v)); }), { commit: true }));
    }
    body.appendChild(buttonRow('Free from the walls', () => edit(() => { delete light.anchor; })));
  }
  for (const w of channelWarnings(inst, light, heaterFootprints(inst))) body.appendChild(noteRow(CHANNEL_WARNING_TEXT[w](), 'warn', true));
  lightCommonRows(body, inst, light);
  body.appendChild(deleteRow(() => { pushHistory(); removeFixture(inst, light.id); renderProperties(); invalidate(); }));
}

// ---- Room fixtures: spots and fibre ends (the Spot tool) ----------------------

/** Where a world point falls on a room's ceiling: in the plan, or in one of the
 *  room's reflected ceiling plans — the plan moved by the view's offset, `shift`. */
interface CeilingPlace { room: ProductInstance; local: Vec2; shift: Vec2 }

/** Every place a world point falls on a ceiling that can be edited: the reflected
 *  ceiling plans first (drawn over the plan), then the rooms, topmost first. */
function ceilingPlacesAt(world: Vec2): CeilingPlace[] {
  const out: CeilingPlace[] = [];
  const editable = (r: ProductInstance | null): r is ProductInstance => !!r && !!r.autoBenches && !instLocked(r) && isPolyRoom(r);
  for (const v of [...(state.drawing.ceilingPlans ?? [])].reverse()) {
    const room = rcpRoom(v);
    if (editable(room) && !objUnpickable(v.id)) out.push({ room, local: toInstanceLocal(room, { x: world.x - v.offset.x, y: world.y - v.offset.y }), shift: v.offset });
  }
  for (let i = state.drawing.instances.length - 1; i >= 0; i--) {
    const room = state.drawing.instances[i];
    if (editable(room)) out.push({ room, local: toInstanceLocal(room, world), shift: { x: 0, y: 0 } });
  }
  return out;
}

/** How near (px) a spot put or dragged must come to another's orthogonal
 *  projection to be held on it — in mm at the tool's aim (toolAim.ts `pxAt`:
 *  the plan's zoom, or the 3D view's own scale where the pointer meets the
 *  ceiling in a 3D drag). */
const SPOT_ALIGN_PX = 10;

/** What the Spot tool does under a world point: remove the spot or fibre end
 *  there, or put one at the point — held in line with another spot near its
 *  projection (`from`, unless Alt is down), else OSNAP-snapped — on the first
 *  ceiling it falls on. */
function spotToolTarget(aim: ToolAim, free: boolean): { room: ProductInstance; at: Vec2; shift: Vec2; from: Vec2[]; guides: SpotGuide[]; remove?: SpotLight } | null {
  const world = aim.world;
  const snapped = snapPoint(world, null, pxAt(aim, SNAP_PX));
  for (const pl of ceilingPlacesAt(world)) {
    const { room, shift } = pl;
    const on = spotAt(room, pl.local, pxAt(aim, 4));
    if (on) return { room, at: on.at, shift, from: [], guides: [], remove: on };
    const ring = polyInnerCarved(room);
    if (!pointInPolygon(pl.local, ring)) continue;
    const held = free ? null : spotAlign(room, pl.local, pxAt(aim, SPOT_ALIGN_PX));
    if ((held?.from.length || held?.guides.length) && pointInPolygon(held.at, ring)) {
      state.snapIndicator = null; state.snapIndicatorKind = null;
      return { room, at: held.at, shift, from: held.from, guides: held.guides };
    }
    const local = toInstanceLocal(room, { x: snapped.x - shift.x, y: snapped.y - shift.y });
    return { room, at: pointInPolygon(local, ring) ? local : pl.local, shift, from: [], guides: [] };
  }
  return null;
}

/** A spot's temporary figures, where they are drawn: the spot (room local) as it
 *  is, or would be, in which room and at which place (`shift`), the spots it is
 *  held in line with and the other lines it is held on (`guides`), and itself
 *  (`except`), not a spot to measure to. */
interface SpotAid { instId: string; at: Vec2; shift: Vec2; from: Vec2[]; guides: SpotGuide[]; except?: string }

/** The Spot tool's hover: the spot a click would add (green), with its figures,
 *  or the one it would remove (red). */
export function spotHoverAt(aim: ToolAim): BenchHover | null {
  const t = spotToolTarget(aim, lightTools.spotFree);
  lightTools.spotAid = t && !t.remove ? { instId: t.room.id, at: t.at, shift: t.shift, from: t.from, guides: t.guides } : null;
  if (!t) return null;
  const fibre = t.remove ? !!t.remove.fibre : lightTools.spotToolFibre;
  const r = Math.max((fibre ? FIBRE_DIAMETER : SPOT_DIAMETER) / 2, pxAt(aim, 6));
  return { instId: t.room.id, polys: [circleRing(t.at, r)], kind: t.remove ? 'delete' : 'add', shift: t.shift };
}

/** The Spot tool's area (its row's, ui/tools3d.ts): a fibre kit's projector
 *  while one waits for it, else the spot a click adds or takes out (Alt: free
 *  of the others' lines). */
export function spotAreaAt(e: MouseEvent, aim: ToolAim): BenchHover | null {
  if (kitStep) return kitStepHoverAt(aim);
  lightTools.spotFree = e.altKey;
  return spotHoverAt(aim);
}

/** The Spot tool's press: on the ceiling, a spot (or a fibre end) there; on a
 *  spot, a drag that moves it — or, let go where pressed, takes it out. */
export function downSpotTool(e: MouseEvent, sp: Vec2, aim: ToolAim): boolean {
  const world = aim.world;
  if (state.tool !== 'light-spot' || e.button !== 0) return false;
  const t = spotToolTarget(aim, e.altKey);
  if (!t) return true;
  const { room } = t;
  if (t.remove) {
    startSpotDrag(room, t.remove, toInstanceLocal(room, { x: world.x - t.shift.x, y: world.y - t.shift.y }), t.shift, sp, true);
    return true;
  }
  pushHistory();
  const light: SpotLight = { id: newId('lt'), kind: 'spot', at: { x: Math.round(t.at.x), y: Math.round(t.at.y) }, ...(lightTools.spotToolFibre ? { fibre: true } : {}) };
  (room.lights ??= []).push(light);
  deselectAll();
  selectOne(state, 'instance', room.id);
  selectFeature(state, { kind: 'fixture', instId: room.id, id: light.id });
  // A fibre point is a kit: the next click places its projector.
  if (light.fibre) { kitStep = { roomId: room.id, lightId: light.id }; kitStepHover = null; updateStatus(); }
  state.benchAddHover = kitStep ? null : spotHoverAt(aim);
  if (kitStep) lightTools.spotAid = null;
  renderProperties();
  invalidate();
  return true;
}

/** The Spot tool's click in the 3D view, at plan point `world` on what the view
 *  shows: a fibre kit's projector, a spot there taken out (on the plan a press
 *  on one drags it), else a spot — or a fibre end — put there. */
export function spotClick3d(e: MouseEvent, aim: ToolAim): void {
  if (downKitStep(e, aim)) return;
  const t = spotToolTarget(aim, e.altKey);
  if (t?.remove) {
    pushHistory();
    removeFixture(t.room, t.remove.id);
    selectRoomKeepingFeature(state, t.room.id);
    renderProperties();
    invalidate();
    return;
  }
  downSpotTool(e, { x: 0, y: 0 }, aim);
}

/** The Starry sky tool's click in the 3D view: a kit's projector, else a sky
 *  laid over a room's whole ceiling (or taken off). False while a zone is to be
 *  drawn: that is the plan's. */
export function skyClick3d(e: MouseEvent, aim: ToolAim): boolean {
  if (downKitStep(e, aim)) return true;
  if (lightTools.skyToolDraw || lightTools.skyDraft.length) return false;
  downSkyTool(e, aim);
  return true;
}

/** A spot being dragged: its room and id, where it was and where it was grabbed
 *  (room local, in the place pressed — `shift`), the press on screen, whether it
 *  has moved yet, and — pressed with the Spot tool — whether letting go where
 *  pressed takes it out. */
export let spotDrag: { instId: string; id: string; at0: Vec2; grab: Vec2; shift: Vec2; sp0: Vec2; moved: boolean; removeOnClick: boolean } | null = null;

/** Select a spot and start dragging it (one gesture, DragMode `spot-move`): a
 *  press on it in the plan, a ceiling plan or the 3D view (ui/tools3d.ts, which
 *  then hands its moves the point where the pointer meets the spot's level). */
export function startSpotDrag(room: ProductInstance, spot: SpotLight, grab: Vec2, shift: Vec2, sp: Vec2, removeOnClick: boolean): void {
  beginGesture();
  deselectAll();
  selectOne(state, 'instance', room.id);
  selectFeature(state, { kind: 'fixture', instId: room.id, id: spot.id });
  spotDrag = { instId: room.id, id: spot.id, at0: { ...spot.at }, grab, shift, sp0: sp, moved: false, removeOnClick };
  startDrag('spot-move');
  state.benchAddHover = null;
  lightTools.spotAid = { instId: room.id, at: spot.at, shift, from: [], guides: [], except: spot.id };
  renderProperties();
  invalidate();
}

/** A press on a spot or fibre end at the head of the stack: select it and drag it. */
export function downSpot(e: MouseEvent, sp: Vec2, world: Vec2, head: ObjRef | null): boolean {
  if (head?.kind !== 'fixture' || !head.instId || e.shiftKey || e.ctrlKey || e.metaKey) return false;
  const room = state.drawing.instances.find((i) => i.id === head.instId);
  const spot = room ? spotsOf(room).find((s) => s.id === head.id) : undefined;
  if (!room || !spot) return false;
  startSpotDrag(room, spot, toInstanceLocal(room, world), { x: 0, y: 0 }, sp, false);
  return true;
}

/** A press on a spot in a reflected ceiling plan: select the spot and drag it there. */
export function downRcpSpot(e: MouseEvent, sp: Vec2, world: Vec2, id: string | null | undefined): boolean {
  const v = id ? (state.drawing.ceilingPlans || []).find((x) => x.id === id) : undefined;
  const room = v ? rcpRoom(v) : null;
  if (!v || !room || instLocked(room) || state.disciplineFilters.lighting === false || e.shiftKey || e.ctrlKey || e.metaKey) return false;
  const local = toInstanceLocal(room, { x: world.x - v.offset.x, y: world.y - v.offset.y });
  const spot = spotAt(room, local, 4 / vp.scale);
  if (spot) { startSpotDrag(room, spot, local, v.offset, sp, false); return true; }
  const ch = channelAt(room, local, 4 / vp.scale);
  if (!ch) return false;
  startChannelDrag(room, ch, local, v.offset, sp);
  return true;
}

/** A dragged spot follows the cursor on its room's ceiling, held in line with
 *  another spot near its projection (Alt: free), its figures shown. */
export function dragMoveSpot(e: MouseEvent, sp: Vec2, world: Vec2, aim: ToolAim): boolean {
  const d = spotDrag;
  const room = d ? state.drawing.instances.find((i) => i.id === d.instId) : undefined;
  const spot = room && d ? spotsOf(room).find((s) => s.id === d.id) : undefined;
  if (!d || !room || !spot) return false;
  if (!d.moved && Math.hypot(sp.x - d.sp0.x, sp.y - d.sp0.y) < 4) return false;
  d.moved = true;
  const cur = toInstanceLocal(room, { x: world.x - d.shift.x, y: world.y - d.shift.y });
  const want = { x: d.at0.x + cur.x - d.grab.x, y: d.at0.y + cur.y - d.grab.y };
  const held = e.altKey ? { at: want, from: [], guides: [] } : spotAlign(room, want, pxAt(aim, SPOT_ALIGN_PX), spot.id);
  if (!pointInPolygon(held.at, polyInnerCarved(room))) return false;
  spot.at = { x: Math.round(held.at.x), y: Math.round(held.at.y) };
  lightTools.spotAid = { instId: room.id, at: spot.at, shift: d.shift, from: held.from, guides: held.guides, except: spot.id };
  invalidate();
  return false;
}

/** A spot drag let go: a Spot tool press let go where it began takes the spot out. */
/** A spot drag put back (the caller restores the drawing): nothing is removed. */
export function dropSpotDrag(): void {
  spotDrag = null;
  lightTools.spotAid = null;
}

export function endSpotDrag(): void {
  const d = spotDrag;
  spotDrag = null;
  lightTools.spotAid = null;
  if (!d) return;
  const room = state.drawing.instances.find((i) => i.id === d.instId);
  if (room && !d.moved && d.removeOnClick) {
    removeFixture(room, d.id);
    selectRoomKeepingFeature(state, room.id);
  }
  renderProperties();
}

/** The spot figures drawn this frame, in `RenderOptions.spotAid.figures`' order
 *  (`spotFigureAt` indexes them): the selected spot's carry what a value typed
 *  into them moves — the spot, along the figure's line (`dir`, room local, from
 *  the spot to what it measures to). */
let spotFiguresShown: Array<{ len: number; edit?: FigureEdit }> = [];
/** What a value typed into a figure moves: a spot along the figure's line
 *  (`dir`, room local, from what it moves to what it measures to), or a
 *  channel's segment square to it or its end's gap (`channel`). */
export interface FigureEdit { instId: string; id: string; dir: Vec2; channel?: { kind: 'gap' | 'offset'; seg: number; end?: 0 | 1 } }

/** A figure retyped: its spot moves along the figure's line until the figure
 *  reads `value` (one undo step), kept inside its room. */
export function applySpotFigure(e: FigureEdit & { len: number }, value: number): void {
  if (e.channel) { applyChannelFigure(e, value); return; }
  const room = state.drawing.instances.find((i) => i.id === e.instId);
  const spot = room ? spotsOf(room).find((s) => s.id === e.id) : undefined;
  if (!room || !spot) return;
  const k = e.len - value, at = { x: spot.at.x + e.dir.x * k, y: spot.at.y + e.dir.y * k };
  if (!pointInPolygon(at, polyInnerCarved(room))) { flashStatus(tr('That puts the spot outside its room.')); return; }
  pushHistory();
  spot.at = { x: Math.round(at.x), y: Math.round(at.y) };
  renderProperties();
  invalidate();
}

/** A channel's figure retyped: its segment moves square to it until the figure
 *  reads `value` — one laid along walls through its offset —, or its end's gap is
 *  set; one undo step, refused (a flash) where it would leave its room. */
function applyChannelFigure(e: FigureEdit & { len: number }, value: number): void {
  const room = state.drawing.instances.find((i) => i.id === e.instId);
  const at = room?.lights?.findIndex((l) => l.id === e.id) ?? -1, ch = room && at >= 0 ? room.lights![at] : undefined;
  if (!room || ch?.kind !== 'channel' || !e.channel) return;
  const trial = JSON.parse(JSON.stringify(ch)) as ChannelLight, k = e.len - value;
  if (e.channel.kind === 'gap') setChannelGap(room, trial, e.channel.end ?? 0, value);
  else shiftChannelSegment(room, trial, e.channel.seg, { x: e.dir.x * k, y: e.dir.y * k });
  if (!trial.pts.every((p) => pointInPolygon(p, polyInnerCarved(room)))) { flashStatus(tr('That puts the channel outside its room.')); return; }
  pushHistory();
  room.lights![at] = trial;
  renderProperties();
  invalidate();
}

/** A press on a figure of the selected spot: type the distance it should read. */
export function downSpotFigure(sp: Vec2): boolean {
  const i = spotFigureAt(planHits, sp), fig = i >= 0 ? spotFiguresShown[i] : undefined;
  if (!fig?.edit) return false;
  openFigureEditor({ kind: 'spot', edit: { ...fig.edit, len: fig.len } }, String(Math.round(fig.len)), sp);
  dropDrag();
  return true;
}

/** The spot aids shown now: the Spot tool's or a drag's, else — in Select with
 *  nothing else going on — the selected spot's, in the plan and its room's
 *  ceiling plans (`selected`: a value typed into one of its figures moves it). */
function spotAidsNow(): { aids: SpotAid[]; selected: boolean } {
  if (state.disciplineFilters.lighting === false) return { aids: [], selected: false };
  if (lightTools.spotAid) return { aids: [lightTools.spotAid], selected: false };
  const f = state.tool === 'select' && state.dragMode === 'none' ? selectedFixtureNow() : null;
  if (f?.light?.kind !== 'spot') return { aids: [], selected: false };
  const at = f.light.at, except = f.light.id;
  const views = (state.drawing.ceilingPlans ?? []).filter((v) => v.roomId === f.inst.id && !objHidden(v.id));
  return { aids: [{ x: 0, y: 0 }, ...views.map((v) => v.offset)].map((shift) => ({ instId: f.inst.id, at, shift, from: [], guides: [], except })), selected: true };
}

/** One spot aid as drawn (world, its place's `shift` added): its figures — each
 *  with its line's direction, room local, from the spot to what it measures to —,
 *  the guides from the spots it is held on and the other lines it is held on. */
function spotAidPieces(a: SpotAid): { figures: Array<SpotAidDraw['figures'][number] & { dir: Vec2 | null }>; guides: Array<[Vec2, Vec2]>; holds: SpotGuide[] } | null {
  const room = state.drawing.instances.find((i) => i.id === a.instId);
  if (!room) return null;
  const w = (p: Vec2): Vec2 => { const q = instanceToWorld(room, p); return { x: q.x + a.shift.x, y: q.y + a.shift.y }; };
  return {
    figures: spotFigures(room, a.at, a.except).map((fig) => {
      const len = Math.hypot(fig.b.x - fig.a.x, fig.b.y - fig.a.y);
      return { a: w(fig.a), b: w(fig.b), len, both: fig.to === 'spot', lane: fig.lane, dir: len > 0 ? { x: (fig.b.x - fig.a.x) / len, y: (fig.b.y - fig.a.y) / len } : null };
    }),
    guides: a.from.map((s): [Vec2, Vec2] => [w(s), w(a.at)]),
    holds: a.guides.map((g) => ({ kind: g.kind, a: w(g.a), b: w(g.b) })),
  };
}

/** The spot aid as the 3D view draws it (`paintSpotAid3d`): the plan's first —
 *  the tool's, a drag's, else the selected spot's —, in the room itself (a
 *  ceiling plan's place is the plan's), and the level the spot hangs at there
 *  (mm above the ground: its room's ceiling under it). */
export function spotAid3d(): { aid: SpotAidDraw; z: number } | null {
  const a = spotAidsNow().aids[0];
  const room = a && state.drawing.instances.find((i) => i.id === a.instId);
  const p = a && room ? spotAidPieces({ ...a, shift: { x: 0, y: 0 } }) : null;
  if (!p || !room || (!p.figures.length && !p.guides.length && !p.holds.length)) return null;
  return { aid: { figures: p.figures.map(({ dir: _dir, ...fig }) => fig), guides: p.guides, holds: p.holds }, z: roomBase(room) + ceilingUnder(room, a.at).z };
}

/** The 3D overlay's spot aid (view3d.ts `paintOverlay3d`): the plan's, through
 *  THE painter (render.ts `paintSpotAid`) at the level the spot hangs, `project`
 *  the view's (css px). Seams on the overlay canvas: `data-spot-figures` (the
 *  figures drawn, mm), `data-spot-holds` (the holds' kinds) and
 *  `data-spot-guides` (how many guides from spots held on). */
export function paintSpotAid3d(ctx: CanvasRenderingContext2D, cv: HTMLCanvasElement, project: (p: { x: number; y: number; z: number }) => Vec2 | null): void {
  const s = spotAid3d();
  const drawn = s ? paintSpotAid(ctx, (p) => project({ x: p.x, y: p.y, z: s.z }), s.aid, drawingDimStyle(state.drawing)) : null;
  cv.dataset.spotFigures = drawn && s ? drawn.figures.map((f) => Math.round(s.aid.figures[f.i].len)).join(' ') : '';
  cv.dataset.spotHolds = drawn ? drawn.holds.join(' ') : '';
  cv.dataset.spotGuides = String(drawn?.guides ?? 0);
}

/** The figures drawn (world): the Spot tool's or a drag's, else — with nothing
 *  else going on — the selected spot's, in the plan and its room's ceiling plans. */
export function spotAidWorld(): RenderOptions['spotAid'] {
  const { aids, selected } = spotAidsNow();
  const figures: SpotAidDraw['figures'] = [], guides: Array<[Vec2, Vec2]> = [], holds: SpotGuide[] = [];
  spotFiguresShown = [];
  for (const a of aids) {
    const p = spotAidPieces(a);
    if (!p) continue;
    for (const { dir, ...fig } of p.figures) {
      figures.push(fig);
      // The selected spot's (`except`, and no tool or drag): a value typed moves it.
      spotFiguresShown.push({ len: fig.len, ...(selected && a.except && dir ? { edit: { instId: a.instId, id: a.except, dir } } : {}) });
    }
    guides.push(...p.guides);
    holds.push(...p.holds);
  }
  // The selected channel's, in the plan and its room's ceiling plans — each takes a
  // value typed into it (its segment's offset, an end's gap) —, also while dragged.
  const fc = state.tool === 'select' && (state.dragMode === 'none' || state.dragMode === 'channel-move') && state.disciplineFilters.lighting !== false ? selectedFixtureNow() : null;
  if (fc?.light?.kind === 'channel' && !pickedLightsNow()) {
    const ch = fc.light, views = (state.drawing.ceilingPlans ?? []).filter((v) => v.roomId === fc.inst.id && !objHidden(v.id));
    for (const shift of [{ x: 0, y: 0 }, ...views.map((v) => v.offset)]) {
      const w = (p: Vec2): Vec2 => { const q = instanceToWorld(fc.inst, p); return { x: q.x + shift.x, y: q.y + shift.y }; };
      for (const fig of channelDims(fc.inst, ch.pts, ch.id)) {
        const len = Math.hypot(fig.b.x - fig.a.x, fig.b.y - fig.a.y);
        figures.push({ a: w(fig.a), b: w(fig.b), len, both: fig.to === 'channel', lane: fig.lane });
        spotFiguresShown.push({ len, ...(len > 0 && state.dragMode === 'none' ? { edit: { instId: fc.inst.id, id: ch.id, dir: { x: (fig.b.x - fig.a.x) / len, y: (fig.b.y - fig.a.y) / len }, channel: { kind: fig.kind, seg: fig.seg, ...(fig.end != null ? { end: fig.end } : {}) } } } : {}) });
      }
    }
  }
  // The Channel tool's: the figures of what it would lay or has drawn so far, and
  // the lines its point is held on; a dragged channel's holds.
  const ca = (state.tool === 'light-channel' || state.dragMode === 'channel-move') && state.disciplineFilters.lighting !== false ? lightTools.channelAid : null;
  const croom = ca ? state.drawing.instances.find((i) => i.id === ca.instId) : undefined;
  if (ca && croom) {
    const w = (p: Vec2): Vec2 => { const q = instanceToWorld(croom, p); return { x: q.x + ca.shift.x, y: q.y + ca.shift.y }; };
    for (const path of ca.paths) {
      for (const fig of channelDims(croom, path, ca.except)) {
        const len = Math.hypot(fig.b.x - fig.a.x, fig.b.y - fig.a.y);
        figures.push({ a: w(fig.a), b: w(fig.b), len, both: fig.to === 'channel', lane: fig.lane });
        spotFiguresShown.push({ len });
      }
    }
    if (ca.at) for (const s of ca.from) guides.push([w(s), w(ca.at)]);
    for (const g of ca.guides) holds.push({ kind: g.kind, a: w(g.a), b: w(g.b) });
  }
  // Test seams: the figures drawn, in mm; the lines held on besides spots'.
  canvas.dataset.spotFigures = figures.map((x) => Math.round(x.len)).join(' ');
  canvas.dataset.spotHolds = holds.map((h) => h.kind).join(' ');
  return figures.length || guides.length || holds.length ? { figures, guides, holds } : null;
}

/** A spot's or a fibre end's panel: where it is (world X / Y), how high the
 *  ceiling holds it, how it is aimed, its colour and product, its power. */
function renderSpotPanel(body: HTMLElement, inst: ProductInstance, light: SpotLight): void {
  const edit = (fn: () => void): void => { pushHistory(); fn(); renderProperties(); invalidate(); };
  body.appendChild(sectionTitle(light.fibre ? 'Fibre point' : 'Spot light'));
  const w = instanceToWorld(inst, light.at);
  const place = (x: number, y: number): void => {
    const p = toInstanceLocal(inst, { x, y });
    light.at = { x: Math.round(p.x), y: Math.round(p.y) };
  };
  body.appendChild(numberRow('X (mm)', Math.round(w.x), (v) => edit(() => place(v, w.y)), { commit: true }));
  body.appendChild(numberRow('Y (mm)', Math.round(w.y), (v) => edit(() => place(w.x, v)), { commit: true }));
  const m = spotMount(inst, light);
  body.appendChild(infoRow('Height above the floor', `${Math.round(m.z)} mm`));
  if (!light.fibre) {
    body.appendChild(selectRow('Aim', [['ceiling', 'Square to the ceiling'], ['plumb', 'Plumb']], light.aimPlumb ? 'plumb' : 'ceiling', (v) => edit(() => {
      if (v === 'plumb') light.aimPlumb = true; else delete light.aimPlumb;
    })));
  }
  if (light.fibre) renderKitProjector(body, inst, light);
  else {
    body.appendChild(selectRow('Colour', [['2700K', '2700 K white'], ['RGB', 'RGB']], light.colour ?? '2700K', (v) => edit(() => {
      if (v === 'RGB') light.colour = 'RGB'; else delete light.colour;
      setLightProduct(light, undefined);
    })));
    body.appendChild(lightProductRow(light, 'spot'));
    body.appendChild(infoRow('Power', `${lightWatts(light, 1).toFixed(1)} W`));
  }
  lightCommonRows(body, inst, light);
  body.appendChild(deleteRow(() => { pushHistory(); removeFixture(inst, light.id); renderProperties(); invalidate(); }));
}

/** A starry sky's panel: its fibres — the kit, the zone, how far they keep off
 *  the walls and the other lights, Reshuffle — and its projector. */
function renderSkyPanel(body: HTMLElement, inst: ProductInstance, light: SkyLight): void {
  const edit = (fn: () => void): void => { pushHistory(); fn(); renderProperties(); invalidate(); };
  const f = skyFigures(light);
  body.appendChild(sectionTitle('Starry sky'));
  body.appendChild(selectRow('Kit', [['50', '50 fibres'], ['100', '100 fibres']], String(skyCount(light)), (v) => edit(() => {
    light.fibres = v === '50' ? 50 : 100;
    // The fibres a smaller kit no longer has let go of where they were dragged.
    if (light.moved) for (const k of Object.keys(light.moved)) if (+k >= light.fibres) delete light.moved[+k];
  })));
  body.appendChild(selectRow('Zone', [['ceiling', 'Whole ceiling'], ['drawn', 'Drawn polygon']], light.zone ? 'drawn' : 'ceiling', (v) => {
    if (v === 'ceiling') { edit(() => { delete light.zone; delete light.moved; }); return; }
    // Drawn: the Starry sky tool draws this sky's zone again.
    setTool('light-sky');
    lightTools.skyToolDraw = true; lightTools.skyRezoneId = light.id;
    updateStatus();
  }));
  if (!light.zone) body.appendChild(numberRow('Margin from the walls (mm)', f.margin, (v) => edit(() => { light.margin = Math.max(0, Math.round(v)); delete light.moved; }), { commit: true }));
  body.appendChild(numberRow('Clear of other lights (mm)', f.clear, (v) => edit(() => { light.clear = Math.max(0, Math.round(v)); }), { commit: true }));
  body.appendChild(buttonRow('Reshuffle', () => edit(() => { light.seed = Math.floor(Math.random() * 2 ** 31); delete light.moved; })));
  const n = skyFibres(inst, light).length;
  body.appendChild(infoRow('Fibre ends', `${n} · Ø ${SKY_FIBRE_DIAMETER} mm`));
  if (!n) body.appendChild(infoRow('', tr('The margin leaves no room for fibres.')));
  renderKitProjector(body, inst, light);
  lightCommonRows(body, inst, light);
  body.appendChild(deleteRow(() => { pushHistory(); removeFixture(inst, light.id); renderProperties(); invalidate(); }));
}

/** The sky whose margin handles show: with the Starry sky tool armed (and no zone
 *  or projector waiting), the selected room's; in Select, the selected sky. */
export function marginHandleSky(): { room: ProductInstance; sky: SkyLight } | null {
  if (state.tool === 'light-sky') {
    const room = selectedInstance();
    const sky = room && !instLocked(room) && !kitStep && !lightTools.skyDraft.length ? skiesOf(room)[0] : undefined;
    return room && sky ? { room, sky } : null;
  }
  const f = selectedFixtureNow();
  return f?.light?.kind === 'sky' ? { room: f.inst, sky: f.light } : null;
}

/** A whole-ceiling sky's margin grips (`skyMarginHandles`), in the plan and in
 *  each of its room's ceiling plans: one dragged square to its edge — by 10 mm,
 *  Alt by 1 — or a figure typed sets the margin from every wall (the fibres
 *  dragged by hand let go, as the panel's field does). */
export function skyMarginGrips(room: ProductInstance, sky: SkyLight): SelGrip[] {
  if (state.disciplineFilters.lighting === false) return [];
  const m0 = skyFigures(sky).margin;
  const set = (v: number): null => {
    sky.margin = Math.max(0, Math.round(v));
    delete sky.moved;
    return null;
  };
  const views = (state.drawing.ceilingPlans ?? []).filter((v) => v.roomId === room.id && !objUnpickable(v.id));
  return [{ x: 0, y: 0 }, ...views.map((v) => v.offset)].flatMap((shift) => skyMarginHandles(room, sky).map(({ at, n }): SelGrip => {
    const w = instanceToWorld(room, at);
    return {
      at: { x: w.x + shift.x, y: w.y + shift.y }, others: [], self: room.id, apply: () => {},
      drag: (world, free) => {
        const p = toInstanceLocal(room, { x: world.x - shift.x, y: world.y - shift.y });
        const v = m0 + (p.x - at.x) * n.x + (p.y - at.y) * n.y;
        set(free ? v : Math.round(v / 10) * 10);
      },
      figure: { label: 'Margin from the walls (mm)', set },
      measure: () => {
        const m = skyFigures(sky).margin, d = m - m0;
        const a = instanceToWorld(room, { x: at.x - n.x * m0, y: at.y - n.y * m0 }), b = instanceToWorld(room, { x: at.x + n.x * d, y: at.y + n.y * d });
        return { a: { x: a.x + shift.x, y: a.y + shift.y }, b: { x: b.x + shift.x, y: b.y + shift.y }, len: m };
      },
    };
  }));
}

/** A selected backrest's grips at its two ends, in the middle of its boards: one
 *  dragged along its face — by 10 mm from that end of the face, Alt by 1, held at
 *  a stop near the cursor (the face's ends, a bench's, a partition's faces) — or
 *  a figure typed (its distance from that end of the face) moves that end alone.
 *  Back on its bench's span, it follows its bench again. */
export function backrestGrips(room: ProductInstance, br: Backrest): SelGrip[] {
  if (instLocked(room)) return [];
  const run = backrestRun(room, br);
  if (!run) return [];
  const f = backrestFigures(br), mid = f.standOff + f.boardThickness / 2;
  const { face } = run, stops = backrestStops(room, br);
  const W = (s: number, d: number): Vec2 => instanceToWorld(room, facePoint(face, s, d));
  const put = (span: { start: number; end: number } | null): void => {
    if (span) { br.start = span.start; br.end = span.end; } else { delete br.start; delete br.end; }
  };
  return ([0, 1] as const).map((k): SelGrip => {
    const edit = (v: number): { left: number } | { right: number } => (k ? { right: v } : { left: v });
    const set = (v: number): null => { put(backrestSpanFor(run, edit(v))); return null; };
    return {
      at: W(k ? run.s1 : run.s0, mid), others: [], self: room.id, apply: () => {},
      drag: (world, free) => {
        const p = toInstanceLocal(room, world);
        const s = (p.x - face.o.x) * face.u.x + (p.y - face.o.y) * face.u.y;
        const stop = free ? undefined : stops.find((t) => Math.abs(t - s) <= 10 / vp.scale);
        const v = k ? face.hi - (stop ?? s) : (stop ?? s) - face.lo;
        set(free || stop != null ? v : Math.round(v / 10) * 10);
      },
      figure: { label: k ? 'Distance from right (mm)' : 'Distance from left (mm)', set },
      measure: () => {
        const r = backrestRun(room, br);
        if (!r) return null;
        const e = k ? r.s1 : r.s0, from = k ? r.face.hi : r.face.lo;
        return { a: W(from, 0), b: W(e, 0), len: Math.abs(e - from) };
      },
    };
  });
}

/** A selected strip's grips at its run's free ends that are its own: one dragged
 *  along the run — by 10 mm, Alt by 1 — or a figure typed sets how far short of
 *  that end the run stops (`stripInsetAt`, never past the other end's inset). */
export function stripEndGrips(room: ProductInstance, light: StripLight): SelGrip[] {
  if (state.disciplineFilters.lighting === false || instLocked(room)) return [];
  const run = stripRuns(room).find((r) => r.ids.includes(light.id));
  return (run?.free ?? []).filter((f) => f.id === light.id).map((f: StripFreeEnd): SelGrip => {
    const w = (p: Vec2): Vec2 => instanceToWorld(room, p);
    const set = (v: number): null => { light[f.field] = Math.max(0, Math.min(f.max, Math.round(v))); return null; };
    const tip = trimPath(f.path, f.inset, 0)[0] ?? f.path[0];
    return {
      at: w(tip), others: [], self: room.id, apply: () => {},
      drag: (world, free) => { const v = stripInsetAt(f, toInstanceLocal(room, world)); set(free ? v : Math.round(v / 10) * 10); },
      figure: { label: f.field === 'insetStart' ? 'Inset at the start (mm)' : 'Inset at the end (mm)', set },
      measure: () => {
        const v = light[f.field] ?? STRIP_INSET;
        return { a: w(f.path[0]), b: w(trimPath(f.path, v, 0)[0] ?? f.path[0]), len: v };
      },
    };
  });
}

/** A selected channel's grips, in the plan and its room's ceiling plans: one in
 *  the middle of each segment moves it square to itself — one laid along walls,
 *  its offset — by 10 mm (Alt: 1), or takes the offset from the wall typed; one at
 *  each free end sets its gap to the wall; a drawn channel's corners drag as a
 *  line's vertices do. Each works from the channel as it was when pressed. */
export function channelGrips(room: ProductInstance, ch: ChannelLight): SelGrip[] {
  if (state.disciplineFilters.lighting === false || instLocked(room) || ch.pts.length < 2) return [];
  const pts0 = ch.pts.map((p) => ({ ...p })), anchor0 = ch.anchor ? JSON.stringify(ch.anchor) : null;
  const reset = (): void => { ch.pts = pts0.map((p) => ({ ...p })); if (anchor0) ch.anchor = JSON.parse(anchor0) as ChannelAnchor; };
  const last = pts0.length - 1, ring = isRingPath(pts0);
  const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
  const step = (v: number, free: boolean): number => (free ? v : Math.round(v / 10) * 10);
  const wallFigure = (k: number, pts: Vec2[]): ChannelDim | undefined => channelDims(room, pts, ch.id).find((f) => f.kind === 'offset' && f.seg === k && f.to === 'wall');
  const views = (state.drawing.ceilingPlans ?? []).filter((v) => v.roomId === room.id && !objUnpickable(v.id));
  const out: SelGrip[] = [];
  for (const shift of [{ x: 0, y: 0 }, ...views.map((v) => v.offset)]) {
    const W = (p: Vec2): Vec2 => { const q = instanceToWorld(room, p); return { x: q.x + shift.x, y: q.y + shift.y }; };
    const L = (w: Vec2): Vec2 => toInstanceLocal(room, { x: w.x - shift.x, y: w.y - shift.y });
    for (let k = 0; k < last; k++) {
      const a = pts0[k], b = pts0[k + 1], len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len < 1) continue;
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, n = { x: -(b.y - a.y) / len, y: (b.x - a.x) / len };
      out.push({
        at: W(mid), others: [], self: room.id, apply: () => {},
        drag: (world, free) => {
          reset();
          const d = step(dot({ x: L(world).x - mid.x, y: L(world).y - mid.y }, n), free);
          shiftChannelSegment(room, ch, k, { x: n.x * d, y: n.y * d });
        },
        figure: {
          label: 'Offset from the walls (mm)',
          set: (v) => {
            reset();
            const f = wallFigure(k, pts0);
            if (f) { const l = Math.hypot(f.b.x - f.a.x, f.b.y - f.a.y), u = { x: (f.b.x - f.a.x) / l, y: (f.b.y - f.a.y) / l }; shiftChannelSegment(room, ch, k, { x: u.x * (l - v), y: u.y * (l - v) }); }
            return null;
          },
        },
        measure: () => { const f = wallFigure(k, ch.pts); return f ? { a: W(f.a), b: W(f.b), len: Math.hypot(f.b.x - f.a.x, f.b.y - f.a.y) } : null; },
      });
    }
    if (!ring) {
      for (const end of [0, 1] as const) {
        const p = end ? pts0[last] : pts0[0], q = end ? pts0[last - 1] : pts0[1], l = Math.hypot(p.x - q.x, p.y - q.y);
        if (l < 1) continue;
        const u = { x: (p.x - q.x) / l, y: (p.y - q.y) / l }, g0 = channelEndGap(room, pts0, end);
        out.push({
          at: W(p), others: [], self: room.id, apply: () => {},
          drag: (world, free) => {
            reset();
            if (!Number.isFinite(g0)) return;
            const t = dot({ x: L(world).x - p.x, y: L(world).y - p.y }, u);
            setChannelGap(room, ch, end, Math.max(0, step(g0 - t, free)));
          },
          figure: { label: end ? 'Gap at the end (mm)' : 'Gap at the start (mm)', set: (v) => { reset(); setChannelGap(room, ch, end, v); return null; } },
          measure: () => {
            const e = end ? ch.pts[ch.pts.length - 1] : ch.pts[0], g = channelEndGap(room, ch.pts, end);
            return Number.isFinite(g) ? { a: W(e), b: W({ x: e.x + u.x * g, y: e.y + u.y * g }), len: g } : null;
          },
        });
      }
    }
    // A drawn channel's corners (a ring's first, its last with it).
    if (!ch.anchor) {
      for (let i = ring ? 0 : 1; i < last; i++) {
        const others = pts0.filter((_, j) => j !== i && !(ring && i === 0 && j === last)).map(W);
        out.push({
          at: W(pts0[i]), others, prev: W(pts0[i ? i - 1 : last - 1]), self: room.id,
          apply: (q) => {
            reset();
            const l = L(q), r = { x: Math.round(l.x), y: Math.round(l.y) };
            ch.pts[i] = r;
            if (ring && i === 0) ch.pts[last] = { ...r };
          },
        });
      }
    }
  }
  return out;
}

/** A channel being dragged: its room and id, where it was grabbed (room local, in
 *  the place pressed — `shift`), it and its anchor as pressed, the press on
 *  screen and whether it has moved yet. */
export let channelDrag: { instId: string; id: string; grab: Vec2; shift: Vec2; pts0: Vec2[]; anchor0: string | null; sp0: Vec2; moved: boolean } | null = null;

/** Select a channel and start dragging it (one gesture). */
function startChannelDrag(room: ProductInstance, ch: ChannelLight, grab: Vec2, shift: Vec2, sp: Vec2): void {
  beginGesture();
  deselectAll();
  selectOne(state, 'instance', room.id);
  selectFeature(state, { kind: 'fixture', instId: room.id, id: ch.id });
  channelDrag = { instId: room.id, id: ch.id, grab, shift, pts0: ch.pts.map((p) => ({ ...p })), anchor0: ch.anchor ? JSON.stringify(ch.anchor) : null, sp0: sp, moved: false };
  startDrag('channel-move');
  renderProperties();
  invalidate();
}

/** A press on a channel at the head of the stack: select it and drag it. */
export function downChannel(e: MouseEvent, sp: Vec2, world: Vec2, head: ObjRef | null): boolean {
  if (head?.kind !== 'fixture' || !head.instId || e.shiftKey || e.ctrlKey || e.metaKey) return false;
  const room = state.drawing.instances.find((i) => i.id === head.instId);
  const ch = room ? channelsOf(room).find((c) => c.id === head.id) : undefined;
  if (!room || !ch) return false;
  startChannelDrag(room, ch, toInstanceLocal(room, world), { x: 0, y: 0 }, sp);
  return true;
}

/** A dragged channel follows the cursor by 10 mm, its first point held where a
 *  channel's point lines up (`channelAlign`; Alt: free on both counts) — one laid
 *  along walls only square to its first wall —, kept inside its room. */
export function dragMoveChannel(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const d = channelDrag;
  const room = d ? state.drawing.instances.find((i) => i.id === d.instId) : undefined;
  const at = room && d ? (room.lights ?? []).findIndex((l) => l.id === d.id) : -1;
  if (!d || !room || at < 0) return false;
  if (!d.moved && Math.hypot(sp.x - d.sp0.x, sp.y - d.sp0.y) < 4) return false;
  d.moved = true;
  const cur = toInstanceLocal(room, { x: world.x - d.shift.x, y: world.y - d.shift.y });
  // By 10 mm, as a grip moves it (Alt: free).
  const step = (v: number): number => (e.altKey ? v : Math.round(v / 10) * 10);
  let delta = { x: step(cur.x - d.grab.x), y: step(cur.y - d.grab.y) };
  const p0 = { x: d.pts0[0].x + delta.x, y: d.pts0[0].y + delta.y };
  const held = e.altKey ? null : channelAlign(room, p0, CHANNEL_ALIGN_PX / vp.scale, { except: d.id });
  if (held) delta = { x: delta.x + held.at.x - p0.x, y: delta.y + held.at.y - p0.y };
  const trial: ChannelLight = { ...(room.lights![at] as ChannelLight), pts: d.pts0.map((p) => ({ ...p })), ...(d.anchor0 ? { anchor: JSON.parse(d.anchor0) as ChannelAnchor } : {}) };
  moveChannel(room, trial, delta);
  if (!trial.pts.every((p) => pointInPolygon(p, polyInnerCarved(room)))) return false;
  room.lights![at] = trial;
  lightTools.channelAid = held && (held.from.length || held.guides.length) ? { instId: room.id, shift: d.shift, paths: [], from: held.from, guides: held.guides, at: held.at } : null;
  invalidate();
  return false;
}

/** A channel drag let go. */
export function endChannelDrag(): void {
  channelDrag = null;
  lightTools.channelAid = null;
  renderProperties();
}

/** With the Starry sky tool armed, a press on a margin grip works it. */
export function downSkyMarginGrip(e: MouseEvent, sp: Vec2): boolean {
  if (state.tool !== 'light-sky' || e.button !== 0) return false;
  return downGrip(e, sp, gripAt(sp));
}

/** A fibre kit's projector rows: its product, where it is placed, its colour,
 *  the distance to its furthest fibre and the arcs drawn — or, gone, a button
 *  that puts one back. */
function renderKitProjector(body: HTMLElement, inst: ProductInstance, light: FibreKit): void {
  const edit = (fn: () => void): void => { pushHistory(); fn(); renderProperties(); invalidate(); };
  body.appendChild(sectionTitle('Projector'));
  const part = kitProjector(light);
  if (!part) {
    body.appendChild(infoRow('Projector', tr('None')));
    body.appendChild(buttonRow('Place a projector', () => edit(() => { setKitProjector(inst, light, projectorHome(inst, light)); })));
    return;
  }
  // The part takes the product chosen: its name, size and colour; it stays put.
  const takeProduct = (t: ProductTemplate | undefined): void => {
    if (!t) return;
    Object.assign(part, { templateId: t.id, name: t.name, nameI18n: t.nameI18n, color: t.color, width: t.width || PROJECTOR_SIZE.width, depth: t.depth || PROJECTOR_SIZE.depth, height: t.height ?? PROJECTOR_SIZE.height });
  };
  body.appendChild(lightProductRow(light, 'projector', takeProduct));
  const inside = projectorInside(inst, part);
  body.appendChild(selectRow('Placed', [['above', 'Above the ceiling'], ['outside', 'Outside the room']], inside ? 'above' : 'outside', (v) => edit(() => {
    const home = projectorHome(inst, light);
    setKitProjector(inst, light, v === 'above' ? home : projectorOutside(inst, toInstanceLocal(inst, { x: part.x, y: part.y })) ?? home);
  })));
  body.appendChild(selectRow('Colour', [['2700K', '2700 K white'], ['RGB', 'RGB']], light.colour ?? '2700K', (v) => edit(() => {
    if (v === 'RGB') light.colour = 'RGB'; else delete light.colour;
    setLightProduct(light, undefined);
    const t = lightProduct(light);
    takeProduct(t);
    setLightProduct(light, t);
  })));
  const from = projectorMiddle(inst, part), local = { x: from[0], y: from[1] };
  const fibres = kitFibres(inst, light);
  const far = furthestFibre(from, fibres);
  body.appendChild(infoRow(light.kind === 'sky' ? 'Furthest fibre' : 'Distance to the projector', `${(far / 1000).toFixed(2)} m`));
  if (light.kind === 'sky') body.appendChild(infoRow('Fibre arcs drawn', tr('1 per {n} fibres · {k}').replace('{n}', String(FIBRES_PER_ARC)).replace('{k}', String(fibreArcTargets(local, fibres.map((x) => x.at)).length))));
  body.appendChild(infoRow('Power', `${lightWatts(light, 1).toFixed(1)} W`));
}

// ---- Room fixtures: the starry sky (the Starry sky tool) -------------------------

/** A fibre kit just laid (a sky, a fibre point), waiting for its projector: the
 *  next click places it. With the prompt's figures as the cursor puts them. */
export let kitStep: { roomId: string; lightId: string } | null = null;
let kitStepHover: { inside: boolean; far: number } | null = null;

/** The kit waiting for its projector, while it is still in the drawing. */
function kitStepKit(): { room: ProductInstance; light: FibreKit } | null {
  if (!kitStep) return null;
  const { roomId, lightId } = kitStep;
  const room = state.drawing.instances.find((i) => i.id === roomId);
  const light = room?.lights?.find((l) => l.id === lightId);
  return room && light && isFibreKit(light) ? { room, light } : null;
}

/** Where a kit's projector stands if put at a point of its room (local): its
 *  middle (room local, mm above the room's floor) and whether it is inside. */
function projectorSpot(room: ProductInstance, light: FibreKit, local: Vec2): { from: Vec3; inside: boolean } {
  const inside = pointInPolygon(local, resolvedOutline(room, 0));
  const base = projectorBase(room, inside ? local : projectorHome(room, light));
  return { from: [local.x, local.y, base + PROJECTOR_SIZE.height / 2], inside };
}

/** The kit step's hover: the projector where a click would put it, and its arcs
 *  (green); the prompt says where that is and how far the furthest fibre is. */
export function kitStepHoverAt(aim: ToolAim): BenchHover | null {
  const world = aim.world;
  const k = kitStepKit();
  if (!k) { kitStep = null; return null; }
  const local = toInstanceLocal(k.room, world);
  const { from, inside } = projectorSpot(k.room, k.light, local);
  const fibres = kitFibres(k.room, k.light);
  const far = furthestFibre(from, fibres);
  if (!kitStepHover || kitStepHover.inside !== inside || Math.round(kitStepHover.far / 10) !== Math.round(far / 10)) {
    kitStepHover = { inside, far };
    updateStatus();
  }
  const hw = PROJECTOR_SIZE.width / 2, hd = PROJECTOR_SIZE.depth / 2;
  const box = [{ x: local.x - hw, y: local.y - hd }, { x: local.x + hw, y: local.y - hd }, { x: local.x + hw, y: local.y + hd }, { x: local.x - hw, y: local.y + hd }];
  const arcs = kitArcs(k.room, from, fibres).map((arc) => pathOutline(arc.map(([x, y]) => ({ x, y })), pxAt(aim, 3)));
  return { instId: k.room.id, polys: [box, ...arcs], kind: 'add' };
}

export function downKitStep(e: MouseEvent, aim: ToolAim): boolean {
  const world = aim.world;
  if (!kitStep || (state.tool !== 'light-sky' && state.tool !== 'light-spot') || e.button !== 0) return false;
  finishKitStep(world);
  return true;
}

/** Place the waiting kit's projector: where the click is (world), or — null:
 *  Enter, Esc, another tool — at its default place, above the ceiling. Inside the
 *  undo step the kit was laid in; the tool stays armed. */
export function finishKitStep(world: Vec2 | null): void {
  const k = kitStepKit();
  kitStep = null; kitStepHover = null;
  if (k) {
    setKitProjector(k.room, k.light, world ? toInstanceLocal(k.room, world) : projectorHome(k.room, k.light));
    noteChangeInStep();
  }
  state.benchAddHover = null;
  updateStatus(); renderProperties(); invalidate();
}

/** The kit step's prompt. */
export function kitStepHint(): string {
  const h = kitStepHover;
  if (!h) return tr('projector — click where it goes: in the room it stands above the ceiling, outside it beside the room; Enter puts it over the middle');
  return (h.inside ? tr('projector above the ceiling, the furthest fibre {m} m away: click to put it here') : tr('projector outside the room, the furthest fibre {m} m away: click to put it here'))
    .replace('{m}', (h.far / 1000).toFixed(2));
}

/** What the Starry sky tool does at a world point: remove the room's sky (a room
 *  takes one), or lay one over the ceiling there — the topmost room that can be edited. */
function skyToolTarget(world: Vec2): { room: ProductInstance; remove?: SkyLight } | null {
  const room = channelRoomAt(world);
  if (!room) return null;
  const sky = skiesOf(room)[0];
  return sky && sky.id !== lightTools.skyRezoneId ? { room, remove: sky } : { room };
}

/** A corner of a sky's zone being drawn: snapped as a polygon's. */
function skySnap(world: Vec2, alt: boolean): Vec2 {
  return snapDraftPoint(world, [...lightTools.skyDraft], { shift: alt, allow45: true, lastSeg: draftSeg(lightTools.skyDraft) });
}

/** The Starry sky tool's hover: the room's sky (red: a click removes it), else the
 *  zone a click scatters the kit over (green); drawing a zone, the snapped point. */
export function skyHoverAt(e: MouseEvent, aim: ToolAim): BenchHover | null {
  const world = aim.world;
  if (lightTools.skyToolDraw || lightTools.skyDraft.length) { state.hoverWorld = skySnap(world, e.altKey); updateDistEntry(); }
  if (lightTools.skyDraft.length) return null;
  const t = skyToolTarget(world);
  if (!t) return null;
  if (t.remove) {
    const zone = skyZone(t.room, t.remove);
    return zone.length >= 3 ? { instId: t.room.id, polys: [zone], kind: 'delete' } : null;
  }
  if (lightTools.skyToolDraw) return null;
  const zone = skyZone(t.room, { id: '', kind: 'sky', fibres: lightTools.skyToolKit, seed: 0 });
  return zone.length >= 3 ? { instId: t.room.id, polys: [zone], kind: 'add' } : null;
}

/** The Starry sky tool's area (its row's): a fibre kit's projector while one
 *  waits for it, else the sky or the zone a click lays — none over a grip of
 *  the plan's (it takes the press). */
export function skyAreaAt(e: MouseEvent, aim: ToolAim): BenchHover | null {
  if (kitStep) return kitStepHoverAt(aim);
  return aim.sp && gripAt(aim.sp) ? null : skyHoverAt(e, aim);
}

export function downSkyTool(e: MouseEvent, aim: ToolAim): boolean {
  const world = aim.world;
  if (state.tool !== 'light-sky' || e.button !== 0) return false;
  if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
  if (!lightTools.skyDraft.length) {
    const t = skyToolTarget(world);
    if (!t) return true;
    if (t.remove) {
      pushHistory();
      removeFixture(t.room, t.remove.id);
      selectRoomKeepingFeature(state, t.room.id);
      state.benchAddHover = skyHoverAt(e, aim);
      renderProperties(); invalidate();
      return true;
    }
    if (!lightTools.skyToolDraw) { addSky(t.room); return true; }
    lightTools.skyRoomId = t.room.id;
  }
  clickPolyline(skySnap(world, e.altKey));
  updateStatus();
  invalidate();
  return true;
}

/** Lay a sky of the tool's kit over a room's ceiling (or a drawn zone, room
 *  local), from a fresh seed, one undo step with its projector: the next click
 *  places that. */
function addSky(room: ProductInstance, zone?: Vec2[]): void {
  pushHistory();
  const light: SkyLight = { id: newId('lt'), kind: 'sky', fibres: lightTools.skyToolKit, seed: Math.floor(Math.random() * 2 ** 31), ...(zone ? { zone } : {}) };
  (room.lights ??= []).push(light);
  deselectAll();
  selectOne(state, 'instance', room.id);
  selectFeature(state, { kind: 'fixture', instId: room.id, id: light.id });
  kitStep = { roomId: room.id, lightId: light.id };
  kitStepHover = null;
  state.benchAddHover = null;
  updateStatus(); renderProperties(); invalidate();
}

/** The zone drawn corner by corner closes: a new sky over it, or — redrawn from a
 *  sky's panel — that sky's zone, its hand-moved fibres let go. */
export function finishSkyZone(): void {
  const room = lightTools.skyRoomId ? state.drawing.instances.find((i) => i.id === lightTools.skyRoomId) : null;
  const pts = lightTools.skyDraft;
  lightTools.skyDraft = []; lightTools.skyRoomId = null;
  if (room && pts.length >= 3) {
    const zone = pts.map((p) => { const q = toInstanceLocal(room, p); return { x: Math.round(q.x), y: Math.round(q.y) }; });
    const again = skiesOf(room).find((s) => s.id === lightTools.skyRezoneId);
    if (again) {
      pushHistory();
      again.zone = zone; delete again.moved;
      lightTools.skyRezoneId = null; lightTools.skyToolDraw = false;
      deselectAll();
      selectOne(state, 'instance', room.id);
      selectFeature(state, { kind: 'fixture', instId: room.id, id: again.id });
      setTool('select');
      renderProperties();
    } else addSky(room, zone);
  }
  updateStatus();
  invalidate();
}

/** The Starry sky tool's prompt. */
export function skyToolHint(): string {
  if (kitStep) return kitStepHint();
  if (lightTools.skyDraft.length) return tr('starry sky — click the zone’s corners; Enter, right-click or double-click to close it');
  const kit = tr('{n} fibres').replace('{n}', String(lightTools.skyToolKit));
  return (lightTools.skyToolDraw ? tr('starry sky, {kit} — click in a room’s ceiling to start drawing the zone; click a sky to remove it') : tr('starry sky, {kit} — click a room’s ceiling to scatter the fibres over it; click a sky to remove it'))
    .replace('{kit}', kit);
}

// ---- Room fixtures: strip lights (the Strip tool) -----------------------------

/** What each strip position is, as the panel and the tool's prompt say it. */
const STRIP_POS_LABELS: Record<StripPos, string> = {
  A1: 'A1 under the front edge',
  A2: 'A2 at the toe-kick',
  B: 'B at the back of the seat',
  C: 'C behind the backrest’s top edge',
  D: 'D behind the backrest’s bottom edge',
};
export const isStripPos = (v: string): v is StripPos => (STRIP_POSITIONS as readonly string[]).includes(v);

/** Which way the armed Strip tool's strips shine: the down tool (A1, A2, D)
 *  or the up tool (B, C); null when neither is armed. */
export function stripToolFacing(): 'up' | 'down' | null {
  return state.tool === 'light-strip' ? 'down' : state.tool === 'light-strip-up' ? 'up' : null;
}

/** What a click of the Strip tool would do now — the strip as it would be drawn
 *  — for its hover and prompt, and its key: both are worked out again only when
 *  that changes. */
let stripToolHover: { room: ProductInstance; target: StripTarget; pts: Vec2[]; len: number; both?: Vec2[][] } | null = null;
/** Where the Strip tool was last aimed, so a typed position shows at once. */
export let stripToolAim: ToolAim | null = null;

const polylineLength = (pts: readonly Vec2[]): number => pts.slice(1).reduce((s, p, i) => s + Math.hypot(p.x - pts[i].x, p.y - pts[i].y), 0);

/** The strip the Strip tool would add or remove under a world point, in the
 *  topmost room that can be edited. */
function stripToolTarget(aim: ToolAim): { room: ProductInstance; target: StripTarget } | null {
  const world = aim.world;
  for (let i = state.drawing.instances.length - 1; i >= 0; i--) {
    const room = state.drawing.instances[i];
    if (!room.autoBenches || instLocked(room) || !isPolyRoom(room)) continue;
    const local = toInstanceLocal(room, world);
    if (!pointInPolygon(local, polyInnerCarved(room))) continue;
    // In 3D, what the click is on decides: the bench, or the backrest over it.
    const bench = aimedBench(aim), fixture = aimedFixture(aim);
    const accept = bench ? (h: StripHost): boolean => 'bench' in h && h.bench === bench
      : fixture && room.backrests?.some((b) => b.id === fixture) ? (h: StripHost): boolean => 'backrest' in h && h.backrest === fixture
        : undefined;
    if (lightTools.stripToolBoth) {
      const target = stripTargetAt(room, local, 'C', undefined, 0, 'up', accept);
      return target && 'backrest' in target.host ? { room, target } : null;
    }
    const target = stripTargetAt(room, local, lightTools.stripToolPos, undefined, 0, stripToolFacing() ?? undefined, accept);
    return target && ('backrest' in target.host || target.host.bench) ? { room, target } : null;
  }
  return null;
}

/** The Strip tool's hover: the strip a click would add (green) — as it would be
 *  drawn, its insets and joins at corners worked out with it — or remove (red). */
export function stripHoverAt(aim: ToolAim): BenchHover | null {
  stripToolAim = aim;
  const t = stripToolTarget(aim);
  const key = t ? `${t.room.id}|${JSON.stringify(t.target.host)}|${t.target.pos}|${t.target.existing ?? ''}|${lightTools.stripToolBoth}|${drawingRev()}` : '';
  if (key !== lightTools.stripToolHoverKey) {
    lightTools.stripToolHoverKey = key;
    stripToolHover = null;
    if (t && lightTools.stripToolBoth && 'backrest' in t.target.host) {
      // Both strips as they would be drawn, the ones there already as they are.
      const probe: ProductInstance = { ...t.room, lights: (t.room.lights ?? []).map((l) => ({ ...l })) };
      const id = t.target.host.backrest, lit = backrestLighting(t.room, id);
      setBackrestLighting(probe, id, 'both', () => `probe-${Math.random()}`);
      const mine = new Set(stripsOf(probe).filter((l) => 'backrest' in l.host && l.host.backrest === id).map((l) => l.id));
      const pieces = stripPieces(probe).filter((p) => mine.has(p.id));
      const pts = pieces.find((p) => p.run.pos === 'C')?.pts ?? t.target.pts;
      stripToolHover = { ...t, target: { ...t.target, ...(lit === 'both' ? { existing: t.target.existing ?? id } : { existing: undefined }) }, pts, len: polylineLength(pts), both: pieces.map((p) => p.pts) };
    } else if (t) {
      const id = t.target.existing ?? '';
      const probe: ProductInstance = id ? t.room : { ...t.room, lights: [...(t.room.lights ?? []), { id, kind: 'strip', pos: t.target.pos, host: t.target.host }] };
      const pts = stripPieces(probe).find((p) => p.id === id)?.pts ?? t.target.pts;
      stripToolHover = { ...t, pts, len: polylineLength(pts) };
    }
    updateStatus();
  }
  const h = stripToolHover;
  if (!h || h.pts.length < 2) return null;
  const w = Math.max(STRIP_WIDTH, pxAt(aim, 8));
  return { instId: h.room.id, polys: (h.both ?? [h.pts]).filter((p) => p.length >= 2).map((p) => pathOutline(p, w)), kind: h.target.existing ? 'delete' : 'add' };
}

/** The Strip tool's prompt: what a click does where the cursor is. */
export function stripToolHint(): string {
  const h = stripToolHover;
  if (lightTools.stripToolBoth) {
    if (!h) return tr('strip lights up + down — hover a backrest to light it both ways');
    return (h.target.existing ? tr('strip lights up + down — {m} m each: click to remove them') : tr('strip lights up + down — {m} m each: click to add them'))
      .replace('{m}', (h.len / 1000).toFixed(2));
  }
  if (!h) {
    if (lightTools.stripToolPos) return tr('strip light — hover a bench or a backrest to put a {pos} strip there').replace('{pos}', lightTools.stripToolPos);
    return stripToolFacing() === 'up'
      ? tr('strip light shining up — hover a bench (B behind its seat) or a backrest (C over its boards), or type B or C')
      : tr('strip light shining down — hover a bench (A1 under its front edge) or a backrest (D under its boards), or type A1, A2 or D');
  }
  const m = (h.len / 1000).toFixed(2);
  return (h.target.existing ? tr('strip light — {what}, {m} m: click to remove it') : tr('strip light — {what}, {m} m: click to add it'))
    .replace('{what}', tr(STRIP_POS_LABELS[h.target.pos])).replace('{m}', m);
}

export function downStripTool(e: MouseEvent, aim: ToolAim): boolean {
  if (!stripToolFacing() || e.button !== 0) return false;
  const t = stripToolTarget(aim);
  if (!t) return true;
  pushHistory();
  const { room, target } = t;
  if (lightTools.stripToolBoth && 'backrest' in target.host) {
    const id = target.host.backrest;
    const added = setBackrestLighting(room, id, backrestLighting(room, id) === 'both' ? 'none' : 'both', () => newId('lt'));
    deselectAll();
    selectOne(state, 'instance', room.id);
    if (added.length) selectFeature(state, { kind: 'fixture', instId: room.id, id: added[0] });
  } else if (target.existing) {
    removeFixture(room, target.existing);
    selectRoomKeepingFeature(state, room.id);
  } else {
    const light: StripLight = { id: newId('lt'), kind: 'strip', pos: target.pos, host: target.host };
    (room.lights ??= []).push(light);
    if (light.pos === 'A1') setBackSkirtingFor(room, light.host);
    deselectAll();
    selectOne(state, 'instance', room.id);
    selectFeature(state, { kind: 'fixture', instId: room.id, id: light.id });
  }
  lightTools.stripToolHoverKey = null;
  state.benchAddHover = stripHoverAt(aim);
  renderProperties();
  invalidate();
  return true;
}

/** The bench a strip is on, as it is stored: a wall bench or a partition's tier. */
function stripBenchSpec(room: ProductInstance, host: StripHost): PolyBench | ProductBenchLeg | null {
  if (!('bench' in host)) return null;
  const wall = room.polyBenches?.find((b) => b.id === host.bench);
  if (wall) return wall;
  for (const iw of room.innerWalls ?? []) {
    const leg = [...(iw.benches?.a ?? []), ...(iw.benches?.b ?? [])].find((l) => l.id === host.bench);
    if (leg) return leg;
  }
  return null;
}

/** An A1 strip hides behind its bench's front board: adding one sets the bench's
 *  skirting `A1_SKIRTING_SET_BACK` behind it, unless its set-back was given — but
 *  not where the front board hangs below the seat: there it sits in the channel
 *  carved into the board and the closure flush behind it. */
function setBackSkirtingFor(room: ProductInstance, host: StripHost): void {
  const spec = stripBenchSpec(room, host);
  if (spec && spec.skirtingSetBack == null && frontBoard(spec) && !frontTakesChannel(spec)) spec.skirtingSetBack = A1_SKIRTING_SET_BACK;
}

/** Where a strip is, as the panel says it: its bench's wall or partition, or its backrest. */
function stripHostLabel(inst: ProductInstance, host: StripHost): string {
  if ('backrest' in host) return `${tr('Backrest')} ${backrestsOf(inst).findIndex((b) => b.id === host.backrest) + 1}`;
  const wall = inst.polyBenches?.find((b) => b.id === host.bench);
  if (wall) return `${tr('Bench')} · ${tr('wall')} ${wall.edge + 1}`;
  const k = (inst.innerWalls ?? []).findIndex((iw) => [...(iw.benches?.a ?? []), ...(iw.benches?.b ?? [])].some((l) => l.id === host.bench));
  return `${tr('Bench')} · ${tr('inner wall')} ${k + 1}`;
}

/** A lighting product by id: the catalogue's, else the built-in generic. */
export function lightTemplate(id: string): ProductTemplate | undefined {
  return state.templates.find((t) => t.id === id) ?? BUILTIN_PRODUCTS.find((t) => t.id === id);
}

/** A light's Product list — or several lights' of one kind and colour —: the
 *  lighting products of its kind made in its colour; a choice sets them all. */
function lightProductRow(given: RoomLight | RoomLight[], kind: 'projector' | 'spot' | 'strip', onChange?: (t: ProductTemplate | undefined) => void): HTMLElement {
  const lights = Array.isArray(given) ? given : [given], light = lights[0];
  const colour = light.colour ?? '2700K';
  const seen = new Set<string>();
  const list = [...state.templates, ...BUILTIN_PRODUCTS].filter((t) => {
    if (seen.has(t.id) || t.category !== 'lighting' || t.lightKind !== kind || (t.lightColour ?? '2700K') !== colour) return false;
    seen.add(t.id);
    return true;
  });
  const row = document.createElement('div'); row.className = 'prop-row';
  const lab = document.createElement('label'); lab.textContent = tr('Product');
  const sel = document.createElement('select');
  for (const t of list) {
    const opt = document.createElement('option');
    opt.value = t.id; opt.textContent = productName(t, getUiLang());
    sel.appendChild(opt);
  }
  const ids = new Set(lights.map((l) => lightProduct(l)?.id ?? ''));
  if (ids.size > 1) {
    const opt = document.createElement('option');
    opt.value = ''; opt.textContent = tr('(mixed)');
    sel.prepend(opt);
    sel.value = '';
  } else if ([...ids][0]) sel.value = [...ids][0];
  sel.addEventListener('change', () => {
    if (!sel.value) return;
    pushHistory();
    const t = lightTemplate(sel.value);
    for (const l of lights) {
      setLightProduct(l, t);
      l.productId = sel.value;
    }
    onChange?.(t);
    renderProperties(); invalidate();
  });
  row.append(lab, sel);
  return row;
}

// ---- Fibre kits and their projector part ----------------------------------------

/** The projector part a fibre kit names, while it is in the drawing. */
function kitProjector(light: FibreKit): ProductInstance | undefined {
  return light.projectorId ? state.drawing.instances.find((i) => i.id === light.projectorId) : undefined;
}

/** Whether a projector stands over its room — above the ceiling — not outside it. */
function projectorInside(room: ProductInstance, part: ProductInstance): boolean {
  return pointInPolygon(toInstanceLocal(room, { x: part.x, y: part.y }), resolvedOutline(room, 0));
}

/** Put a fibre kit's projector at a point of its room (local): inside the room on
 *  the ceiling's top there, outside it as high as over the kit's middle. Moves the
 *  one the kit has, else makes one — an ordinary lighting part of the kit's
 *  product — and names it. No history of its own. */
export function setKitProjector(room: ProductInstance, light: FibreKit, local: Vec2): ProductInstance {
  const inside = pointInPolygon(local, resolvedOutline(room, 0));
  const base = roomBase(room) + projectorBase(room, inside ? local : projectorHome(room, light));
  const at = instanceToWorld(room, local);
  let part = kitProjector(light);
  if (!part) {
    const t = lightProduct(light) ?? lightTemplate(defaultLightProductId(light))!;
    part = {
      id: newId('inst'), templateId: t.id, name: t.name, nameI18n: t.nameI18n, category: t.category,
      x: at.x, y: at.y, rotation: room.rotation, width: t.width || PROJECTOR_SIZE.width, depth: t.depth || PROJECTOR_SIZE.depth,
      color: t.color, height: t.height ?? PROJECTOR_SIZE.height, properties: t.properties.map((p) => ({ ...p })),
      preparations: (t.preparations || []).map((p) => ({ ...p, id: newId('prep') })), ports: copyPorts(t.ports),
    };
    state.drawing.instances.push(part);
    light.projectorId = part.id;
    setLightProduct(light, t);
  }
  part.x = at.x; part.y = at.y;
  part.heightFromGround = Math.round(base);
  return part;
}

/** The projector part of a kit taken out goes with it — unless another light
 *  still names it. */
export function dropKitProjector(light: FibreKit): void {
  const pid = light.projectorId, d = state.drawing;
  if (!pid || !d.instances.some((i) => i.id === pid)) return;
  if (d.instances.some((r) => (r.lights ?? []).some((l) => l !== light && isFibreKit(l) && l.projectorId === pid))) return;
  removeObjectsById(new Set([pid]));
  dropGoneInstancesFromSelection();
}

/** A light's own panel, by its kind. */
export function renderLightPanel(body: HTMLElement, inst: ProductInstance, light: RoomLight): void {
  switch (light.kind) {
    case 'strip': renderStripPanel(body, inst, light); return;
    case 'spot': renderSpotPanel(body, inst, light); return;
    case 'channel': renderChannelPanel(body, inst, light); return;
    case 'cove': renderCovePanel(body, inst, light); return;
    case 'sky': renderSkyPanel(body, inst, light); return;
    default: unreachable(light, 'light kind');
  }
}

/** A strip's panel: where it is, its position (any its host offers) and the
 *  way it shines, its colour and the products made in it, its insets, an A1's
 *  skirting set-back, then its length and power, derived. */
function renderStripPanel(body: HTMLElement, inst: ProductInstance, light: StripLight): void {
  const edit = (fn: () => void): void => { pushHistory(); fn(); renderProperties(); invalidate(); };
  body.appendChild(sectionTitle('Strip light'));
  body.appendChild(infoRow('On', stripHostLabel(inst, light.host)));
  body.appendChild(selectRow('Position', hostPositions(inst, light.host).map((p) => [p, STRIP_POS_LABELS[p]] as const), light.pos, (v) => edit(() => {
    if (!isStripPos(v)) return;
    light.pos = v;
    if (v === 'A1') setBackSkirtingFor(inst, light.host);
  })));
  const run = stripRuns(inst).find((r) => r.ids.includes(light.id));
  if (run) body.appendChild(infoRow('Shines', run.shine.tilt === 0 ? tr(run.shine.up ? 'Straight up' : 'Straight down') : tr(run.shine.up ? 'Up, {deg}° toward the room' : 'Down, {deg}° toward the room').replace('{deg}', String(run.shine.tilt))));
  body.appendChild(selectRow('Colour', [['2700K', '2700 K white'], ['RGB', 'RGB']], light.colour ?? '2700K', (v) => edit(() => {
    if (v === 'RGB') light.colour = 'RGB'; else delete light.colour;
    setLightProduct(light, undefined);
  })));
  body.appendChild(lightProductRow(light, 'strip'));
  body.appendChild(numberRow('Inset at the start (mm)', light.insetStart ?? STRIP_INSET, (v) => edit(() => { light.insetStart = Math.max(0, Math.round(v)); }), { commit: true }));
  body.appendChild(numberRow('Inset at the end (mm)', light.insetEnd ?? STRIP_INSET, (v) => edit(() => { light.insetEnd = Math.max(0, Math.round(v)); }), { commit: true }));
  const spec = light.pos === 'A1' ? stripBenchSpec(inst, light.host) : null;
  if (spec && frontTakesChannel(spec)) {
    // In the channel carved into the front board and the closure flush behind it.
    body.appendChild(infoRow('Mounting', tr('LED channel carved into the front board')));
    const need = channelHangNeeded(spec), hang = frontBoard(spec)?.hang ?? 0;
    if (hang < need - 1e-6) body.appendChild(noteRow(tr('The front board is too short for the channel’s groove: it must hang {n} mm below the seat.').replace('{n}', String(Math.round(need))), 'warn', true));
  } else if (spec && frontBoard(spec)) {
    body.appendChild(numberRow('Skirting set back (mm)', spec.skirtingSetBack ?? 0, (v) => edit(() => { spec.skirtingSetBack = Math.max(0, Math.round(v)); }), { commit: true }));
  }
  const len = lightLength(inst, light);
  body.appendChild(infoRow('Length', `${(len / 1000).toFixed(2)} m`));
  body.appendChild(infoRow('Power', `${lightWatts(light, len).toFixed(1)} W`));
  if (run && run.ids.length > 1) {
    body.appendChild(infoRow('Run', tr('{n} strips joined, {m} m').replace('{n}', String(run.ids.length)).replace('{m}', (run.length / 1000).toFixed(2))));
  }
  lightCommonRows(body, inst, light);
  body.appendChild(deleteRow(() => { pushHistory(); removeFixture(inst, light.id); renderProperties(); invalidate(); }));
}

/** The light tag painted under a screen point, the topmost. */
function lightTagAt(sp: Vec2): LightTagHit | null {
  for (let i = planHits.lightTags.length - 1; i >= 0; i--) {
    const b = planHits.lightTags[i];
    if (sp.x >= b.x - 2 && sp.x <= b.x + b.w + 2 && sp.y >= b.y - 2 && sp.y <= b.y + b.h + 2) return b;
  }
  return null;
}

/** A press on a light's tag: select its light and drag the tag (one gesture) —
 *  from where it is drawn, so the first move keeps it under the cursor. */
export function downLightTag(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (state.tool !== 'select' || e.button !== 0 || e.shiftKey || e.ctrlKey || e.metaKey) return false;
  const hit = lightTagAt(sp);
  const room = hit ? state.drawing.instances.find((i) => i.id === hit.instId) : undefined;
  const anchor = room && hit ? lightTagAnchors(room).find((a) => a.id === hit.id) : undefined;
  if (!hit || !room || !anchor || instLocked(room)) return false;
  deselectAll();
  selectOne(state, 'instance', room.id);
  selectFeature(state, { kind: 'fixture', instId: room.id, id: hit.id });
  beginGesture();
  const c = toInstanceLocal(room, vp.screenToWorld({ x: hit.x + hit.w / 2, y: hit.y + hit.h / 2 }));
  lightTools.lightTagDrag = { instId: room.id, id: hit.id, grab: toInstanceLocal(room, world), o0: { x: c.x - anchor.at.x, y: c.y - anchor.at.y }, sp0: sp, moved: false };
  startDrag('light-tag');
  renderProperties();
  invalidate();
  return true;
}

/** A dragged tag follows the cursor, kept on its light (`tagOffset`, room local). */
export function dragMoveLightTag(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const d = lightTools.lightTagDrag;
  const room = d ? state.drawing.instances.find((i) => i.id === d.instId) : undefined;
  const light = room && d ? room.lights?.find((l) => l.id === d.id) : undefined;
  if (!d || !room || !light) return false;
  if (!d.moved && Math.hypot(sp.x - d.sp0.x, sp.y - d.sp0.y) < 3) return false;
  d.moved = true;
  const p = toInstanceLocal(room, world);
  light.tagOffset = { x: Math.round(d.o0.x + p.x - d.grab.x), y: Math.round(d.o0.y + p.y - d.grab.y) };
  invalidate();
  return false;
}

/** Every light panel's last rows before Delete: Select all like this (the lights
 *  of its room under its tag, picked together) and — its tag moved — Reset tag
 *  position, back beside its light. */
function lightCommonRows(body: HTMLElement, inst: ProductInstance, light: RoomLight): void {
  const key = lightTagKey(light), like = (inst.lights ?? []).filter((l) => lightTagKey(l) === key);
  if (like.length > 1) {
    body.appendChild(buttonRow('Select all like this', () => { pickLights(inst.id, light.id, like.map((l) => l.id)); renderProperties(); invalidate(); }));
  }
  if (light.tagOffset) body.appendChild(buttonRow('Reset tag position', () => { pushHistory(); delete light.tagOffset; renderProperties(); invalidate(); }));
}

// ---- Several lights of a room picked together -----------------------------------

/** The lights of a room picked together (Shift+click, Select all like this), `id`
 *  the one whose panel leads; two or more, the panel edits them together. None:
 *  the light selection goes. */
function pickLights(instId: string, id: string, ids: readonly string[]): void {
  if (!ids.length) { dropFeature(state, 'fixture'); return; }
  selectFeature(state, { kind: 'fixture', instId, id: ids.includes(id) ? id : ids[ids.length - 1], picks: [...ids] });
}

/** The lights picked together — two or more —, while the selected fixture is one
 *  of them and its room is still the selection. */
export function pickedLightsNow(): { inst: ProductInstance; lights: RoomLight[] } | null {
  const f = selectedFixtureNow(), picks = featureOf(state, 'fixture')?.picks;
  if (!f?.light || !picks || !picks.includes(f.id)) return null;
  const lights = (f.inst.lights ?? []).filter((l) => picks.includes(l.id));
  return lights.length > 1 ? { inst: f.inst, lights } : null;
}

/** Shift (or Ctrl) + click on a light of the selected light's room: picked with
 *  it, or — picked already — let go. */
export function downLightToggle(e: MouseEvent, world: Vec2): boolean {
  if (e.button !== 0 || !(e.shiftKey || e.ctrlKey || e.metaKey) || state.tool !== 'select') return false;
  const f = selectedFixtureNow(), hit = f?.light ? fixtureHitTest(world) : null;
  if (!f || !hit || hit.instId !== f.inst.id || !f.inst.lights?.some((l) => l.id === hit.id)) return false;
  const ids = new Set(pickedLightsNow()?.lights.map((l) => l.id) ?? [f.id]);
  if (ids.has(hit.id)) ids.delete(hit.id); else ids.add(hit.id);
  pickLights(f.inst.id, hit.id, [...ids]);
  dropDrag();
  renderProperties();
  invalidate();
  return true;
}

/** Several lights picked together: how many of each, the colour and the product
 *  they share — a choice sets them all, one undo step —, Delete. A fibre kit's
 *  colour is its projector's, so with one among them there is no Colour. */
export function renderLightsPanel(body: HTMLElement, inst: ProductInstance, lights: RoomLight[]): void {
  const edit = (fn: () => void): void => { pushHistory(); fn(); renderProperties(); invalidate(); };
  body.appendChild(sectionTitle(`${lights.length} ${tr('lights selected')}`));
  const names: Record<RoomLight['kind'], string> = { strip: 'Strip light', spot: 'Spot light', channel: 'Channel', cove: 'Cove', sky: 'Starry sky' };
  const count = new Map<string, number>();
  for (const l of lights) { const n = l.kind === 'spot' && l.fibre ? 'Fibre point' : names[l.kind]; count.set(n, (count.get(n) ?? 0) + 1); }
  for (const [n, k] of count) body.appendChild(infoRow(n, `× ${k}`));
  if (!lights.some(isFibreKit)) {
    const colours = new Set(lights.map((l) => l.colour ?? '2700K')), one = colours.size === 1 ? [...colours][0] : '';
    const opts: Array<readonly [string, string]> = [...(one ? [] : [['', '(mixed)'] as const]), ['2700K', '2700 K white'], ['RGB', 'RGB']];
    body.appendChild(selectRow('Colour', opts, one, (v) => {
      if (!v) return;
      edit(() => { for (const l of lights) { if (v === 'RGB') l.colour = 'RGB'; else delete l.colour; setLightProduct(l, undefined); } });
    }));
    const kinds = new Set(lights.map((l) => (l.kind === 'spot' ? 'spot' : 'strip') as 'spot' | 'strip'));
    if (kinds.size === 1 && one) body.appendChild(lightProductRow(lights, [...kinds][0]));
  }
  // Straight channels side by side: spaced evenly, or a spacing typed.
  const chans = lights.filter((l): l is ChannelLight => l.kind === 'channel' && l.pts.length === 2);
  const u0 = chans[0] ? { x: chans[0].pts[1].x - chans[0].pts[0].x, y: chans[0].pts[1].y - chans[0].pts[0].y } : null;
  const l0 = u0 ? Math.hypot(u0.x, u0.y) : 0;
  if (u0 && l0 > 0 && chans.length === lights.length && chans.every((c) => Math.abs((c.pts[1].x - c.pts[0].x) * u0.y - (c.pts[1].y - c.pts[0].y) * u0.x) <= 0.035 * l0 * Math.hypot(c.pts[1].x - c.pts[0].x, c.pts[1].y - c.pts[0].y))) {
    const n = { x: -u0.y / l0, y: u0.x / l0 }, at = chans.map((c) => c.pts[0].x * n.x + c.pts[0].y * n.y);
    const sorted = [...at].sort((p, q) => p - q), gaps = sorted.slice(1).map((c, i) => c - sorted[i]);
    const place = (spacing?: number): void => edit(() => {
      const to = distributedPositions(at, spacing);
      chans.forEach((c, i) => moveChannel(inst, c, { x: n.x * (to[i] - at[i]), y: n.y * (to[i] - at[i]) }));
    });
    body.appendChild(buttonRow('Distribute evenly', () => place()));
    body.appendChild(numberRow('Spacing (mm)', Math.round(gaps[0] ?? 0), (v) => { if (v > 0) place(Math.round(v)); }, { commit: true }));
  }
  body.appendChild(deleteRow(() => edit(() => { for (const l of lights) removeFixture(inst, l.id); dropFeature(state, 'fixture'); })));
}
